import { interiorModules } from "./catalog";
import {
  coverFamilySystems,
  crossSellTemplates,
  evergreenLaunchPlans,
  interiorLayoutMasters,
  leadMagnetSystems,
  resolveCoverPalette,
  welcomeSequences,
} from "./expansion";
import {
  getChannelDelivery,
  getChannelListing,
  getMarketplaceGuide,
  getSellingKitGuide,
  recommendedLayoutIds,
  recommendedModuleIds,
} from "./marketplaces";
import type {
  OfferSku,
  PaperKingdomProject,
} from "../domain/schema";
import {
  getPromptIdFromWorkKey,
  getPromptWorkForChannel,
  getPromptWorkEntriesForChannel,
} from "../lib/promptWork";
import { buildAdaptivePageBlueprint } from "../lib/pageBlueprint";
import {
  extractInteriorRecommendation,
  extractTitleRecommendation,
} from "../lib/promptResults";

export type PromptCategory =
  | "Imprint Strategy"
  | "Market Intelligence"
  | "Catalogue Architecture"
  | "Titles & Subtitles"
  | "Cover Concepts"
  | "Interior Systems"
  | "Back-cover Copy"
  | "Product Descriptions"
  | "Keywords & Categories"
  | "Promotion & Quality"
  | "Cover Production"
  | "Interior Production"
  | "Print & Preflight"
  | "Rights & Handoff"
  | "Listing & Metadata"
  | "A+ Content"
  | "Launch Strategy"
  | "Reader Funnel"
  | "Cross-sell & Retention";

export interface PromptDefinition {
  id: string;
  number: number;
  title: string;
  category: PromptCategory;
  objective: string;
  deliverable: string;
  expertLens: string;
  requiredOffer: OfferSku;
}

type PromptCustomizationInput = {
  targetBookId?: string;
  customInstructions?: string;
};

interface PromptGroup {
  slug: string;
  category: PromptCategory;
  expertLens: string;
  deliverable: string;
  tasks: Array<[title: string, objective: string]>;
}

const promptGroups: PromptGroup[] = [
  {
    slug: "imprint",
    category: "Imprint Strategy",
    expertLens: "Act as a publishing brand strategist who understands small-catalogue positioning, reader trust, and repeatable identity systems.",
    deliverable:
      "Return a decision-ready strategy with a one-sentence recommendation, a concise rationale, specific language to adopt, language to avoid, and a consistency checklist.",
    tasks: [
      ["Imprint positioning statement", "Write a differentiated positioning statement that names the audience, publishing territory, useful promise, and credible reason to believe."],
      ["Reader promise", "Define one durable reader promise that can stretch across the catalogue without becoming vague or making an outcome guarantee."],
      ["Brand territory map", "Map the topics the imprint should own, can occasionally explore, and should deliberately avoid."],
      ["Imprint naming directions", "Develop strategically distinct imprint-name directions and evaluate memorability, fit, pronunciation, extensibility, and risk."],
      ["Voice principles", "Translate the positioning into practical voice principles with positive examples, counterexamples, and editing tests."],
      ["Visual character brief", "Convert the imprint positioning into a precise emotional and visual character brief for designers and image models."],
      ["Credibility architecture", "Identify how the imprint can communicate care and usefulness through specificity, structure, sourcing, and transparent limitations."],
      ["Differentiation audit", "Compare the proposed positioning with predictable category clichés and make it more ownable without forcing novelty."],
      ["Audience boundary", "Define the primary reader, adjacent reader, non-reader, purchase context, and expectations the product should not create."],
      ["Imprint origin story", "Write a grounded, customer-relevant imprint story that explains purpose without inventing credentials or sentimental mythology."],
      ["Naming convention", "Create a repeatable naming system for catalogue families, individual books, editions, and companion assets."],
      ["Tone calibration", "Calibrate the voice across instructional, reflective, promotional, cautionary, and celebratory moments."],
      ["Trust language review", "Audit proposed brand language for exaggeration, false authority, generic claims, and accidental health or income promises."],
      ["One-page brand charter", "Condense the approved imprint decisions into a one-page charter a contributor can apply without further explanation."],
      ["Imprint stress test", "Stress-test whether the imprint system still works across three future markets, formats, and price points."],
    ],
  },
  {
    slug: "market",
    category: "Market Intelligence",
    expertLens: "Act as an ethical publishing-market researcher. Distinguish evidence, hypotheses, and items that require live marketplace verification.",
    deliverable:
      "Return a research plan and insight brief with reader jobs, purchase occasions, competing approaches, opportunity hypotheses, evidence gaps, and validation actions. Do not invent search volume.",
    tasks: [
      ["Reader job analysis", "Identify the functional, emotional, and identity jobs the proposed book may help the target reader perform."],
      ["Purchase occasion map", "Map self-purchase, gift, seasonal, milestone, problem-aware, and collection-completion purchase occasions."],
      ["Category convention scan", "List common category conventions, explain the expectation each creates, and identify which should be kept, improved, or rejected."],
      ["Review-mining protocol", "Design a compliant review-research method for learning from reader praise, frustration, confusion, and unmet expectations."],
      ["Competitor comparison grid", "Create a comparison framework for assessing promise, format, depth, design, proof, price context, and reader fit."],
      ["Opportunity hypothesis", "Develop evidence-seeking opportunity hypotheses rather than unsupported statements about demand or competition."],
      ["Audience language bank", "Build a research-backed language-capture worksheet for the words readers use to describe their context, desired progress, and objections."],
      ["Format-fit assessment", "Evaluate which book or printable formats best support the intended reader behaviour and usage environment."],
      ["Giftability assessment", "Assess whether the concept works as a gift and what clarity, tone, packaging, and recipient cues would be required."],
      ["Seasonality map", "Identify genuine seasonal relevance, evergreen framing, timing risks, and a sensible validation calendar."],
      ["Price-context research", "Create a method for comparing customer-perceived substance and price context without recommending price from fabricated data."],
      ["Problem-awareness ladder", "Map how the concept should speak to readers who are unaware, problem-aware, solution-aware, or actively comparing options."],
      ["Objection research", "Identify likely objections about usefulness, effort, format, originality, quality, and fit, then specify evidence needed to answer them."],
      ["Market-pack expansion", "Turn the core market insight into six adjacent but meaningfully distinct catalogue angles."],
      ["Go/no-go evidence plan", "Define the smallest current-market checks needed before investing in production and the thresholds for revise, proceed, or pause."],
    ],
  },
  {
    slug: "catalogue",
    category: "Catalogue Architecture",
    expertLens: "Act as a catalogue development editor who balances family resemblance, reader progression, commercial clarity, and title-level substance.",
    deliverable:
      "Return a structured catalogue matrix with shared locks, book-level variables, sequence logic, overlap risks, differentiation tests, and future expansion paths.",
    tasks: [
      ["Six-book catalogue matrix", "Design six related books that share one imprint promise while each owns a distinct reader job and use case."],
      ["Catalogue progression", "Arrange the books into a sensible reader progression without implying that low-content books qualify for a marketplace Series feature."],
      ["Differentiation test", "Test whether every planned title has enough distinct purpose, prompts, and interior value to justify separate publication."],
      ["Shared design lock", "Identify which design attributes should remain constant and which should vary so the catalogue is recognizable at thumbnail size."],
      ["Content-overlap audit", "Find duplicated exercises, promises, title language, and reader outcomes across the proposed catalogue."],
      ["Format portfolio", "Assign formats to titles based on reader behaviour rather than making every idea use the same format."],
      ["Catalogue naming system", "Create a naming pattern that signals relationship while keeping title and subtitle language natural and non-repetitive."],
      ["Reader pathway map", "Show credible next-book pathways based on reader needs, not manipulative completion pressure."],
      ["Three-book starter set", "Reduce a broad catalogue idea to the strongest three-title validation set."],
      ["Twelve-title expansion", "Expand an approved six-book system to twelve titles without weakening the positioning or repeating substance."],
      ["Book role definitions", "Assign each title a clear portfolio role such as entry, depth, application, seasonal, gift, reference, or companion."],
      ["Catalogue gap analysis", "Identify missing reader stages, overrepresented angles, and expansion ideas that should be rejected."],
      ["Edition strategy", "Define when an edition, companion, bundle, or genuinely new title is the honest product choice."],
      ["Catalogue quality gate", "Create pass/fail gates a title must clear before moving from concept to production."],
      ["Future-product bridge", "Identify ethical ways the catalogue could later connect to templates, workshops, or services without making the books feel incomplete."],
    ],
  },
  {
    slug: "title",
    category: "Titles & Subtitles",
    expertLens: "Act as a senior publishing copywriter. Prioritize clarity, reader fit, memorability, metadata truth, and cover usability over cleverness.",
    deliverable:
      "Return grouped title options, subtitle options, a scored shortlist, repetition and metadata checks, cover-length notes, and one recommended direction with rationale.",
    tasks: [
      ["Outcome-led title set", "Create clear title options led by the progress or experience the book supports without promising a guaranteed result."],
      ["Concept-led title set", "Create distinctive but understandable title options built around the book's central organizing idea."],
      ["Audience-led title set", "Develop title directions that help the intended reader self-identify without excluding adjacent buyers unnecessarily."],
      ["Gift-friendly title set", "Create title and subtitle options that make recipient, occasion, tone, and usefulness easy to understand."],
      ["Editorial title set", "Develop restrained, bookstore-quality title options that avoid generic marketplace keyword stacking."],
      ["Direct-response title set", "Develop highly clear, benefit-aware options while preserving natural language and metadata integrity."],
      ["Six-title family", "Name six catalogue books so their relationship is visible but the wording and reader purpose of each remain distinct."],
      ["Subtitle clarity pass", "Rewrite draft subtitles to clarify format, audience, content, and use without repetition or exaggerated claims."],
      ["Title length edit", "Shorten difficult cover titles while preserving the most ownable and useful idea."],
      ["Title collision audit", "Identify likely confusion, sameness, pronunciation issues, unintended meanings, and live-search checks required before use."],
      ["Keyword-natural title pass", "Integrate only genuinely descriptive search language while keeping the title readable and credible."],
      ["Title hierarchy brief", "Recommend how title, subtitle, imprint, descriptor, and catalogue marker should work together on the cover."],
      ["Alternative-angle titles", "Create title options for reflective, practical, premium, playful, and gift-oriented versions of the same honest product."],
      ["Title scoring matrix", "Score candidate titles for clarity, distinction, audience fit, cover fit, stretch, tone, and verification risk."],
      ["Final title recommendation", "Synthesize the strongest title and subtitle after checking catalogue fit, cover use, metadata consistency, and unsupported implications."],
    ],
  },
  {
    slug: "cover",
    category: "Cover Concepts",
    expertLens: "Act as an award-winning book art director who can turn positioning into executable cover systems for human designers or image-generation tools.",
    deliverable:
      "Return three distinct cover routes. For each, specify composition, hierarchy, focal device, palette roles, typography character, repeatable series logic, production risks, and a precise image prompt that excludes text unless requested.",
    tasks: [
      ["Hero cover routes", "Generate one finished front-cover artwork image for the lead title using the reader, purpose, format, and only the visual choices the publisher supplied."],
      ["Six-cover system", "Design a family system that remains recognizable across six covers while giving every title a meaningful visual variable."],
      ["Thumbnail legibility", "Rework the cover concept for recognition, hierarchy, and distinction at marketplace thumbnail size."],
      ["Typography-led cover", "Create a cover direction where type, proportion, spacing, and one minimal mark carry the concept."],
      ["Symbolic-object cover", "Identify a non-cliché symbolic object and art direction that communicates the title's specific reader job."],
      ["Abstract cover system", "Develop an abstract visual grammar tied to the book's idea rather than generic decorative shapes."],
      ["Botanical cover system", "Create an original botanical direction with controlled species, composition, print treatment, and catalogue variation."],
      ["Premium gift cover", "Develop a tactile, restrained cover route suitable for a gift context without relying on simulated luxury clichés."],
      ["Playful activity cover", "Create an energetic, age-appropriate cover system that is clear to both the purchaser and end user."],
      ["Genre-expectation audit", "Assess the concept against category cues so it feels relevant without copying recognizable competitor trade dress."],
      ["Spine and back system", "Extend the front-cover direction into a practical spine and back-cover hierarchy with barcode-safe planning."],
      ["Print-risk review", "Identify risks involving fine lines, low contrast, trim, bleed, ink density, spine width, and generative-image artifacts."],
      ["Image-model master prompt", "Write a production-grade image prompt for the focal artwork with composition, materials, lighting, exclusions, and editability."],
      ["Cover variation prompts", "Write six tightly controlled image prompts that preserve one art direction while varying the title-specific subject."],
      ["Cover critique", "Critique a proposed cover against positioning, hierarchy, thumbnail performance, originality, metadata match, and print practicality."],
    ],
  },
  {
    slug: "interior",
    category: "Interior Systems",
    expertLens: "Act as an information designer and guided-publication editor. Design pages around reader behaviour, cognitive load, print practicality, and meaningful progression.",
    deliverable:
      "Return a page-system specification with page purpose, information hierarchy, exact fields, instruction copy, interaction rhythm, repetition rules, accessibility notes, and a sample spread.",
    tasks: [
      ["Interior architecture", "Design the complete interior sequence from front matter through orientation, practice, review, and close."],
      ["Guided journal spread", "Create a guided spread whose questions progress from observation to meaning to a realistic next action."],
      ["Daily page system", "Design a repeatable daily page that is quick to understand and useful even when completed imperfectly."],
      ["Weekly review system", "Create a weekly review that extracts evidence and adjustment rather than rewarding performative productivity."],
      ["Tracker with context", "Design a tracker that records relevant context and patterns instead of reducing progress to a streak."],
      ["Workbook exercise", "Turn an abstract idea into a bounded exercise with clear inputs, decisions, and a usable output."],
      ["Activity-book module", "Design an engaging activity sequence with age-appropriate instructions, variety, and a satisfying completion cue."],
      ["Planning dashboard", "Create a one-spread dashboard that shows commitments, capacity, constraints, and next actions without crowding."],
      ["Reflection progression", "Sequence reflection prompts across a book so they deepen without becoming repetitive or intrusive."],
      ["Section opener", "Write and structure a section opener that orients the reader, sets expectations, and reduces friction."],
      ["Front-matter orientation", "Create concise front matter explaining who the book is for, how to use it, flexibility, and important limitations."],
      ["Closing integration", "Design a closing section that helps the reader notice change, keep evidence, and choose what continues."],
      ["Interior consistency audit", "Audit a proposed module set for duplicated function, weak instructions, uneven density, and visual inconsistency."],
      ["Print accessibility review", "Review type size, contrast, writing space, language complexity, navigation, and motor demands for the intended audience."],
      ["Master-page specification", "Translate the approved modules into a production-ready master-page list with grid, styles, variables, and quality checks."],
    ],
  },
  {
    slug: "back-cover",
    category: "Back-cover Copy",
    expertLens: "Act as a thoughtful book-packaging copywriter. Make the book understandable and desirable without hype, false urgency, or unverifiable claims.",
    deliverable:
      "Return a headline, 120–170 word back-cover description, concise feature list, ideal-reader line, responsible limitation where needed, and a final metadata-alignment check.",
    tasks: [
      ["Core back-cover copy", "Write balanced back-cover copy that leads with the reader's context, explains the book's method, and names concrete contents."],
      ["Problem-aware opening", "Open from a recognizable reader situation without amplifying fear, shame, or inadequacy."],
      ["Aspirational opening", "Open with a specific, grounded picture of the experience the book supports without guaranteeing transformation."],
      ["Gift-positioned copy", "Write copy that helps a gift buyer understand the recipient, tone, usefulness, and what is inside."],
      ["Practical workbook copy", "Position the book as a working tool by explaining decisions, worksheets, and outputs rather than vague inspiration."],
      ["Guided journal copy", "Explain the reflective experience, pacing, prompt style, and flexibility without presenting the book as therapy."],
      ["Activity-book copy", "Describe age fit, activity variety, materials, independence level, and learning style accurately."],
      ["Catalogue-family copy", "Reference related books as coordinated companions without incorrectly claiming marketplace Series eligibility."],
      ["Feature-to-benefit edit", "Translate a raw list of pages and modules into reader-relevant benefits grounded in actual product contents."],
      ["Credibility edit", "Remove inflated claims and replace them with credible specificity, structure, and transparent scope."],
      ["Short back-cover version", "Condense the approved copy for a small trim size while protecting hierarchy and substance."],
      ["Premium tone version", "Refine the copy into a restrained, editorial, giftable voice without becoming vague or ornate."],
      ["Warm direct version", "Refine the copy into an accessible, encouraging voice while keeping specificity and adult intelligence."],
      ["Back-cover hierarchy", "Organize headline, paragraphs, bullets, imprint line, and barcode zone into a practical reading order."],
      ["Back-cover compliance review", "Audit claims, metadata match, audience clarity, AI implications, and promises that require evidence or removal."],
    ],
  },
  {
    slug: "description",
    category: "Product Descriptions",
    expertLens: "Act as an ecommerce editorial copywriter with publishing experience. Optimize comprehension and decision quality before persuasion.",
    deliverable:
      "Return a scannable long description, short description, five accurate bullets, ideal-fit and not-for-you guidance, formatting notes, and a claim-risk review.",
    tasks: [
      ["Marketplace description", "Write a complete product description that accurately connects reader context, intended progress, method, and contents."],
      ["AIDA description", "Use attention, interest, desire, and action subtly, without formulaic hype or manufactured urgency."],
      ["Problem–method–contents", "Structure the listing around a real reader problem, the book's working method, and exactly what the buyer receives."],
      ["Gift-buyer description", "Make the purchase decision easy for someone selecting the book for another person."],
      ["Mobile-first description", "Reformat the listing so its essential truth and differentiation survive fast mobile scanning."],
      ["Short product summary", "Condense the product into a clear summary suitable for cards, catalogues, and checkout line items."],
      ["Feature bullets", "Write benefit-aware bullets that remain provably tied to actual modules and physical or digital contents."],
      ["Ideal-reader section", "Describe the reader situations and preferences that make the book a strong fit."],
      ["Expectation-setting section", "Explain what the book is not, what it does not replace, and the effort or materials it reasonably requires."],
      ["Catalogue cross-sell", "Write a useful related-title section based on different reader jobs rather than generic buy-more language."],
      ["Premium editorial version", "Create a concise, high-trust description with an editorial cadence and minimal promotional friction."],
      ["Warm conversational version", "Create a friendly version that feels human without filler, forced intimacy, or unsupported reassurance."],
      ["Description differentiation", "Compare six catalogue descriptions and rewrite them so each title's purpose is immediately distinct."],
      ["Description evidence audit", "Mark every claim as product fact, reasonable interpretation, hypothesis, or unsupported claim requiring removal."],
      ["Final listing synthesis", "Combine the strongest approved elements into a final description with no repeated promises or metadata contradictions."],
    ],
  },
  {
    slug: "keywords",
    category: "Keywords & Categories",
    expertLens: "Act as a careful marketplace-research assistant. Never invent keyword volume, competition scores, category availability, or current policy facts.",
    deliverable:
      "Return seed phrases grouped by intent, a live-research checklist, relevance and readability filters, negative terms, category hypotheses to verify, and a documented final-selection rubric.",
    tasks: [
      ["Seed keyword universe", "Generate a broad but relevant seed list from audience, use case, format, topic, outcome, style, and purchase occasion."],
      ["Reader-intent clusters", "Group search phrases by what the shopper appears to be trying to find or decide."],
      ["Format-specific phrases", "Identify natural phrases that accurately signal the book's format and usage pattern."],
      ["Gift-intent phrases", "Develop relevant gift-search hypotheses without inserting occasions or recipients the product does not fit."],
      ["Long-tail research plan", "Turn broad topics into specific phrase patterns for live marketplace and search-engine validation."],
      ["Keyword naturalness edit", "Remove awkward, duplicated, misleading, or overly broad phrases from a candidate keyword list."],
      ["Catalogue cannibalization check", "Assess whether six titles are targeting identical search intent and propose honest distinctions."],
      ["Negative keyword list", "Identify misleading audiences, outcomes, formats, professions, ages, and regulated claims to exclude."],
      ["Category hypothesis map", "Propose likely category territories, adjacent alternatives, and exact checks required in the current publishing interface."],
      ["Browse-path analysis", "Map how a reader might browse from broad category to specific need and where the book honestly belongs."],
      ["Competitor-language capture", "Create a worksheet for recording recurring descriptive language without copying titles, subtitles, or protected expression."],
      ["Keyword-to-copy bridge", "Show how validated reader language can inform natural description copy without keyword stuffing."],
      ["Seven-field allocation", "Allocate a validated phrase set across available metadata fields with minimal repetition and maximum relevance."],
      ["Keyword verification log", "Create a dated evidence log for autocomplete, result relevance, category availability, and policy review."],
      ["Final metadata research brief", "Synthesize keyword and category hypotheses into a concise human research brief with clear unknowns."],
    ],
  },
  {
    slug: "promotion",
    category: "Promotion & Quality",
    expertLens: "Act as a senior launch editor, creative director, and publishing quality lead. Keep every claim tied to product truth and every recommendation testable.",
    deliverable:
      "Return an execution-ready plan with message hierarchy, assets, copy, schedule, owner, quality gates, compliance checks, and success signals that do not presume sales.",
    tasks: [
      ["Launch message house", "Build a message hierarchy connecting audience context, product promise, mechanism, proof, objections, and responsible call to action."],
      ["Four-week content plan", "Create a useful launch calendar that teaches the product's core ideas rather than repeating sales messages."],
      ["Promotional graphic brief", "Write a production brief for a high-impact graphic with one message, layout hierarchy, art direction, copy, and alt text."],
      ["Email launch sequence", "Plan a five-email sequence with distinct educational purposes, honest urgency, and no income or ranking promises."],
      ["Product demonstration script", "Write a concise walkthrough showing a real project moving from imprint lock to catalogue matrix to exported prompts."],
      ["Objection response bank", "Answer concerns about AI use, originality, credits, complexity, fit, output quality, and marketplace compliance accurately."],
      ["Social proof framework", "Create ethical prompts for collecting specific customer feedback without coaching exaggerated outcomes."],
      ["Seasonal campaign", "Adapt the product to a genuine seasonal purchase context without pretending evergreen products have artificial deadlines."],
      ["Catalogue bundle campaign", "Present several related books as a useful collection while keeping each title's independent value clear."],
      ["Preflight quality audit", "Run a cross-functional audit of content, design, files, metadata, rights, accessibility, proofing, and disclosure decisions."],
      ["AI disclosure worksheet", "Separate AI-generated from AI-assisted contributions and create a record for the publisher's current marketplace disclosure decision."],
      ["Rights verification checklist", "Create a source-by-source rights record for text, fonts, images, templates, references, and model outputs."],
      ["KDP consistency review", "Check cover text, metadata, interior, format, description, and catalogue language for contradictions or unsupported promises."],
      ["Printed-proof review", "Create a physical proof inspection sequence covering trim, gutter, type, ink, writing usability, barcode zone, and reader flow."],
      ["Post-launch learning review", "Design a review that separates observable results from assumptions and turns reader feedback into responsible product improvements."],
    ],
  },
];

const productionPromptGroups: PromptGroup[] = [
  {
    slug: "production-cover",
    category: "Cover Production",
    expertLens:
      "Act as a senior book-cover systems designer and print-production art director. When a publishing identity or cover lock exists, translate it into a repeatable cover family without flattening title-level distinction.",
    deliverable:
      "Return a production-ready cover specification with grid, hierarchy, front/spine/back zones, title-level variables, asset requirements, accessibility checks, thumbnail tests, and explicit handoff notes.",
    tasks: [
      ["Cover-family production system", "Convert the chosen cover style and any recorded family rules into one reusable cover grid with controlled title-level variables."],
      ["Six-title cover mapping", "Map every catalogue title to a distinct cover variant while preserving immediate family recognition."],
      ["Front-cover hierarchy specification", "Define exact reading order, relative scale, alignment, line-break logic, and safe fallback rules for title and subtitle."],
      ["Back-cover information architecture", "Build a back-cover hierarchy for fit, contents, use, imprint, barcode-safe area, and claim-safe persuasion."],
      ["Spine system brief", "Specify spine typography, orientation, imprint marker, colour logic, and narrow-spine fallbacks using confirmed dimensions only."],
      ["Thumbnail recognition test", "Create a practical test for title legibility, family recognition, focal distinction, and misleading similarity at marketplace thumbnail size."],
      ["Cover asset production list", "Create a complete list of original or cleared illustrations, photographs, textures, marks, fonts, and evidence required before design."],
      ["Cover handoff and review brief", "Write the designer handoff, review sequence, file naming, approval gates, and correction rules for the complete catalogue."],
    ],
  },
  {
    slug: "production-interior",
    category: "Interior Production",
    expertLens:
      "Act as an information designer and book-interior production lead. Design around physical use, reader effort, print constraints, accessibility, and meaningful title differentiation.",
    deliverable:
      "Return an implementation-ready interior plan with page sequence, master-page mapping, field specifications, repetition rules, accessibility checks, estimated page logic, and title-specific variation.",
    tasks: [
      ["Interior master-page map", "Map the project to a concise set of reusable master pages and explain when each master may and may not be reused."],
      ["Title-level page sequence", "Create a complete page sequence for one selected title with front matter, sections, exercises, reviews, and closing material."],
      ["Catalogue-wide module allocation", "Allocate interior modules across the catalogue so each book has sufficient substance and a distinct reader job."],
      ["Writing-space and interaction audit", "Evaluate field sizes, writing comfort, cognitive load, instructions, and repeated interaction patterns for the chosen format."],
      ["Front-matter production brief", "Specify title page, copyright, disclosure, usage guidance, contents, and reader-orientation pages without generic filler."],
      ["Navigation system", "Design page numbers, section markers, recurring cues, progress signals, and restart-friendly navigation."],
      ["Interior typography specification", "Define functional type roles, minimum useful sizes, leading, hierarchy, contrast, emphasis, and fallback fonts."],
      ["Interior consistency and overlap audit", "Test whether repeated structures remain useful while content, examples, progression, and purchase reason stay materially distinct."],
    ],
  },
  {
    slug: "production-preflight",
    category: "Print & Preflight",
    expertLens:
      "Act as a print-production manager and preflight specialist. Require verified specifications, distinguish recorded facts from pending checks, and never infer a changing marketplace dimension.",
    deliverable:
      "Return a versioned preflight record with verified inputs, unresolved risks, file checks, proof checks, pass/fail criteria, responsible owner, and exact next actions.",
    tasks: [
      ["Production specification record", "Convert confirmed trim, bleed, paper, page count, spine, barcode, colour, font, and export decisions into one controlled record."],
      ["Cover-file preflight", "Audit cover dimensions, safe areas, spine position, barcode zone, fonts, image resolution, transparency, and expected export settings."],
      ["Interior-file preflight", "Audit page size, margins, gutter, bleed, page order, blank pages, fonts, images, lines, and interactive writing space."],
      ["Physical-proof inspection", "Create an ordered proof review covering trim, alignment, gutter, type, ink, images, writing comfort, barcode, and page flow."],
      ["Format-risk assessment", "Identify production risks specific to the selected format and define the evidence required to clear each one."],
      ["Pre-upload readiness gate", "Create a strict pass, revise, or stop gate using only confirmed specifications and completed checks."],
      ["Post-upload file review", "Plan the previewer inspection and compare the rendered result against the approved production record."],
      ["Final production sign-off", "Create a concise approval record covering version, reviewer, known limitations, rights, disclosure, proof status, and release authorization."],
    ],
  },
  {
    slug: "production-rights",
    category: "Rights & Handoff",
    expertLens:
      "Act as a publishing operations editor focused on provenance, licence evidence, controlled revisions, and clean handoff. Provide operational guidance, not legal conclusions.",
    deliverable:
      "Return a traceable operations package with source records, licence questions, revision history, approval ownership, final file inventory, and archive instructions.",
    tasks: [
      ["Rights and asset ledger", "Build a source-by-source ledger for text, fonts, images, templates, references, and generated material with evidence and restrictions."],
      ["AI contribution record", "Separate AI-generated and AI-assisted contributions and prepare the factual record needed for the publisher's current disclosure review."],
      ["Production revision register", "Create a controlled revision log that prevents ambiguous feedback, overwritten approvals, and untraceable file changes."],
      ["File naming and archive system", "Design a naming, versioning, folder, backup, and approval-reference system for the complete catalogue."],
      ["Production delivery dossier", "Package specifications, final files, rights evidence, proof status, known limitations, and responsibility notes for handoff."],
      ["Catalogue production audit", "Run a final cross-title audit of family consistency, title differentiation, file completeness, metadata alignment, and unresolved risks."],
    ],
  },
];

const launchGrowthPromptGroups: PromptGroup[] = [
  {
    slug: "growth-listing",
    category: "Listing & Metadata",
    expertLens:
      "Act as a senior book metadata editor and ethical marketplace researcher. Align every field with product truth and label every keyword or category idea as a hypothesis until verified live.",
    deliverable:
      "Return a complete listing record with a description, three strongest keyword hypotheses, up to four optional alternatives, audience details, verification steps, consistency checks, and unresolved evidence gaps. Suggest category areas only when current marketplace evidence is available.",
    tasks: [
      ["Complete listing package", "Prepare all listing fields for one title using only confirmed cover, interior, format, audience, and catalogue facts."],
      ["Outcome-led book description", "Write a scannable description that explains reader fit, specific contents, use, limitations, and a responsible invitation."],
      ["Three strong keyword hypotheses", "Develop the three most natural and relevant keyword phrases with intent rationale, duplication checks, and live verification actions."],
      ["Optional keyword alternatives", "Offer up to four additional phrases only when they add distinct customer intent rather than filling space."],
      ["Conditional category research", "When current seller-account evidence is supplied, identify accurate category or shop-section areas and show what must still be confirmed before selection."],
      ["Metadata-to-cover audit", "Compare title, subtitle, author or imprint, format, claims, and visual promise across cover and listing fields."],
      ["Description claim audit", "Remove vague transformation language, unsupported authority, implied guarantees, keyword stuffing, and content not present in the book."],
      ["Catalogue listing differentiation", "Compare every title's description and metadata so related books remain easy to distinguish."],
      ["Marketplace adaptation brief", "Plan how listing language and verification should change across selected marketplaces without inventing local demand."],
      ["Final metadata sign-off", "Create a dated sign-off record with verified facts, live research results, remaining risks, and the exact fields approved for submission."],
    ],
  },
  {
    slug: "growth-aplus",
    category: "A+ Content",
    expertLens:
      "Act as an A+ Content strategist, conversion copy editor, and accessible visual-brief writer. Use only product-supported claims and current module requirements.",
    deliverable:
      "Return a coherent storyboard of no more than five modules with each module's job, copy, image brief, factual inputs, alt-text direction, rights checks, and review criteria.",
    tasks: [
      ["Five-module A+ storyboard", "Design a complete five-module narrative that helps the right reader understand fit, substance, and catalogue context."],
      ["Catalogue comparison module", "Create accurate comparison-table content that distinguishes up to five relevant titles by reader job and contents."],
      ["Inside-the-book module", "Plan a representative interior showcase with cleared page crops, concise captions, and no unreadable or invented text."],
      ["Imprint story module", "Write a factual, reader-relevant imprint introduction without fabricated credentials or sentimental mythology."],
      ["Book mechanism module", "Explain how the reader uses the book and what record or experience the page system supports without guaranteeing outcomes."],
      ["A+ image production briefs", "Specify original image concepts, dimensions to verify, composition, focal hierarchy, alt text, rights, and mobile legibility."],
      ["A+ compliance review", "Audit proposed copy and images for unsupported claims, prohibited language, inaccurate comparison, rights risk, and metadata contradiction."],
      ["Cross-market A+ rollout", "Create a controlled plan for applying and reviewing A+ content across eligible ASINs and marketplaces."],
    ],
  },
  {
    slug: "growth-launch",
    category: "Launch Strategy",
    expertLens:
      "Act as a publishing launch strategist and accountable campaign manager. Build useful, testable work around a finished product without manufacturing urgency or predicting sales.",
    deliverable:
      "Return a launch plan with dependencies, owners, assets, schedule, message hierarchy, budget boundaries, compliance gates, learning signals, and stop or revise conditions.",
    tasks: [
      ["Thirty-day launch plan", "Create a realistic four-week plan covering readiness, education, release, observation, and evidence-led improvement."],
      ["Launch dependency map", "Order listing, files, proof, Author Central, A+ content, promotional assets, and advertising preparation by dependency."],
      ["Author Central readiness", "Prepare the factual biography, book-claiming checklist, catalogue presentation, and profile-review steps."],
      ["Sponsored Products starter brief", "Define campaign objective, eligibility checks, detail-page readiness, targeting hypotheses, budget boundary, and review conditions."],
      ["Launch message house", "Build a claim-safe hierarchy connecting reader context, product promise, mechanism, proof, fit, objections, and invitation."],
      ["Educational campaign sequence", "Create a launch content series that teaches five useful ideas and demonstrates the book's mechanism before asking for a purchase."],
      ["Launch asset production list", "Specify every listing, A+, email, social, demonstration, and review asset with owner and due date."],
      ["Launch risk register", "Identify file, rights, metadata, schedule, moderation, budget, and communication risks with prevention and response actions."],
      ["Launch-day operating checklist", "Create a calm sequence for verifying live pages, links, assets, messages, and support readiness without vanity activity."],
      ["Post-launch learning review", "Separate observations from assumptions and decide what to keep, revise, retest, or stop."],
    ],
  },
  {
    slug: "growth-funnel",
    category: "Reader Funnel",
    expertLens:
      "Act as a reader-experience strategist, lifecycle copywriter, and consent-aware funnel architect. Lead with usefulness, preserve reader choice, and avoid manipulative retention tactics.",
    deliverable:
      "Return a complete reader path with lead magnet, insert, destination copy, consent language, five-email sequence, catalogue routing, exclusions, and measurement notes.",
    tasks: [
      ["Reader funnel architecture", "Map the complete path from book to companion resource, consented follow-up, useful welcome, and relevant catalogue discovery."],
      ["Lead-magnet selection", "Choose the strongest companion-resource format by reader job, immediate usefulness, production effort, and catalogue fit."],
      ["Lead-magnet production brief", "Specify the promise, contents, page sequence, design requirements, rights, quality checks, and responsible next step."],
      ["QR insert copy", "Write concise in-book copy explaining the optional resource, destination, privacy expectation, and exact reader benefit."],
      ["Opt-in page copy", "Write a clear headline, value explanation, form guidance, consent note, delivery expectation, and non-coercive call to action."],
      ["Welcome email one", "Deliver the promised resource clearly and set accurate expectations for future communication."],
      ["Welcome email two", "Teach the subscriber how to use the resource and reduce the most likely starting friction."],
      ["Welcome email three", "Teach one valuable idea that deepens the book's method without repeating promotional copy."],
      ["Welcome email four", "Answer a real fit, use, or limitation question with specific and honest guidance."],
      ["Welcome email five", "Recommend one relevant next step or title with a clear reason it may or may not fit."],
      ["Preference and consent review", "Audit the funnel for transparent consent, accurate delivery, unsubscribe expectations, data minimization, and misleading pressure."],
      ["Reader-funnel quality audit", "Review usefulness, continuity, message consistency, catalogue relevance, broken assumptions, and unresolved operational needs."],
    ],
  },
  {
    slug: "growth-retention",
    category: "Cross-sell & Retention",
    expertLens:
      "Act as a catalogue lifecycle editor. Recommend the next useful product by reader need and evidence, never by pressure, artificial scarcity, or presumed customer value.",
    deliverable:
      "Return a catalogue routing system with triggers, fit logic, exclusion logic, copy, placement, frequency boundaries, measurement, and revision criteria.",
    tasks: [
      ["Catalogue cross-sell map", "Map each title's completed reader job to plausible remaining needs and the most relevant independent next title."],
      ["Completion bridge copy", "Write a respectful end-of-book bridge that recognizes completed work before suggesting an optional next resource."],
      ["Need-based recommendation system", "Create a compact decision tree that routes readers by current context rather than catalogue order."],
      ["Catalogue comparison copy", "Write short, accurate fit statements showing how related titles differ in job, substance, effort, and use occasion."],
      ["Post-purchase support sequence", "Plan five useful support messages that improve use before presenting any adjacent title."],
      ["Reader-question routing", "Turn recurring reader questions into helpful answers and only then identify a genuinely relevant deeper resource."],
      ["Seasonal relevance review", "Identify legitimate seasonal contexts for existing titles without manufacturing deadlines or renaming evergreen products."],
      ["Cross-sell placement audit", "Review inserts, closing pages, email, A+, Author Central, and landing pages for relevance, frequency, and reader control."],
      ["Retention evidence plan", "Define observable engagement signals, limitations, consent boundaries, and review cadence without presuming lifetime value."],
      ["Catalogue growth review", "Use reader questions and observed behaviour to prioritize revisions, new companion resources, and future titles responsibly."],
    ],
  },
];

export const corePrompts: PromptDefinition[] = promptGroups.flatMap((group) =>
  group.tasks.map(([title, objective], index) => ({
    id: `pk-${group.slug}-${String(index + 1).padStart(2, "0")}`,
    number: promptGroups.slice(0, promptGroups.indexOf(group)).reduce((total, item) => total + item.tasks.length, 0) + index + 1,
    title,
    category: group.category,
    objective,
    deliverable:
      title === "Image-model master prompt"
        ? "Return exactly one final image-generation prompt for the selected cover direction. Include the composition, palette, materials or rendering style, empty text zones, aspect ratio, originality constraints, editing needs, and a clear exclusion list. Do not return additional cover routes unless the publisher asks for them."
        : group.deliverable,
    expertLens: group.expertLens,
    requiredOffer: "paper-kingdom-fe" as const,
  })),
);

function buildOfferPrompts(
  groups: PromptGroup[],
  requiredOffer: "paper-kingdom-production" | "paper-kingdom-launch-growth",
  startingNumber: number,
): PromptDefinition[] {
  return groups.flatMap((group) =>
    group.tasks.map(([title, objective], index) => ({
      id: `pk-${group.slug}-${String(index + 1).padStart(2, "0")}`,
      number:
        startingNumber +
        groups.slice(0, groups.indexOf(group)).reduce((total, item) => total + item.tasks.length, 0) +
        index,
      title,
      category: group.category,
      objective,
      deliverable: group.deliverable,
      expertLens: group.expertLens,
      requiredOffer,
    })),
  );
}

export const productionPrompts = buildOfferPrompts(
  productionPromptGroups,
  "paper-kingdom-production",
  151,
);
export const launchGrowthPrompts = buildOfferPrompts(
  launchGrowthPromptGroups,
  "paper-kingdom-launch-growth",
  181,
);
export const allPrompts = [...corePrompts, ...productionPrompts, ...launchGrowthPrompts];

export const promptCategories = [
  ...promptGroups,
  ...productionPromptGroups,
  ...launchGrowthPromptGroups,
].map((group) => group.category);

function projectSummary(project: PaperKingdomProject): string {
  const hasPublishingIdentity =
    Boolean(project.imprint.positioning.trim()) ||
    Boolean(project.imprint.typographyMood.trim()) ||
    Boolean(project.imprint.coverRules.trim()) ||
    Boolean(project.imprint.interiorRules.trim()) ||
    Boolean(project.imprint.voiceRules.trim()) ||
    Boolean(project.imprint.metadataRules.trim()) ||
    Boolean(
      project.imprint.name.trim() &&
        project.imprint.name.trim() !== "My Publishing Name",
    );
  const publishingIdentity = hasPublishingIdentity
    ? `- Publishing name: ${
        project.imprint.name.trim() &&
        project.imprint.name.trim() !== "My Publishing Name"
          ? project.imprint.name
          : "[not chosen]"
      }
- Positioning: ${project.imprint.positioning || "[not chosen]"}
- Lettering feel: ${project.imprint.typographyMood || "[not chosen]"}
- Cover rules: ${project.imprint.coverRules || "[not chosen]"}
- Inside-page rules: ${project.imprint.interiorRules || "[not chosen]"}
- Writing style: ${project.imprint.voiceRules || "[not chosen]"}
- Sales-page rules: ${project.imprint.metadataRules || "[not chosen]"}`
    : "- No optional publishing identity has been chosen. Do not invent one.";

  return `PRODUCT AND READER
- Marketplace: ${project.marketplace}
- Format: ${project.format}
- Primary reader: ${project.audience || "[not yet described]"}
- Core theme: ${project.theme || "[clarify the theme before finalizing]"}
- Book type: ${project.bookType}

OPTIONAL PUBLISHING IDENTITY
${publishingIdentity}`;
}

const bookTargetCategories = new Set<PromptCategory>([
  "Titles & Subtitles",
  "Cover Concepts",
  "Interior Systems",
  "Back-cover Copy",
  "Product Descriptions",
  "Keywords & Categories",
  "Promotion & Quality",
  "Cover Production",
  "Interior Production",
  "Print & Preflight",
  "Listing & Metadata",
  "A+ Content",
  "Launch Strategy",
  "Reader Funnel",
  "Cross-sell & Retention",
]);

const categoryDirectives: Record<PromptCategory, string> = {
  "Imprint Strategy":
    "Treat the imprint as a durable publishing system. Resolve positioning, trust, naming, voice, and extensibility before recommending surface-level creative choices.",
  "Market Intelligence":
    "Separate supplied facts, reasonable hypotheses, and live research requirements. Never present assumed demand, search volume, competition, pricing, or category availability as evidence.",
  "Catalogue Architecture":
    "Evaluate the catalogue as a portfolio. Protect the shared promise while testing sequence logic, reader-job differentiation, overlap risk, and future expansion.",
  "Titles & Subtitles":
    "Keep every title accurate, natural, distinct, legible at cover scale, and aligned with the actual interior. Avoid keyword-stuffed or interchangeable options.",
  "Cover Concepts":
    "Use only a cover style or publishing rules the user actually chose. Create title-specific focal ideas without inventing a brand decision or rendered text.",
  "Interior Systems":
    "Design around the reader's physical interaction with the chosen format. Specify page purpose, hierarchy, fields, rhythm, writing space, accessibility, and repetition logic.",
  "Back-cover Copy":
    "Make the reading path, contents, format, and fit concrete. Use persuasive specificity without fabricated proof, vague transformation language, or unsupported claims.",
  "Product Descriptions":
    "Align every statement with the cover, interior, format, and audience. Prioritize scannability, product truth, clear fit, and useful expectation-setting.",
  "Keywords & Categories":
    "Produce research hypotheses and verification steps, not invented marketplace facts. Preserve natural language and reject misleading intent, audiences, formats, and claims.",
  "Promotion & Quality":
    "Tie every message to demonstrable product truth. Define owners, assets, checks, and learning signals without assuming sales, ranking, acceptance, or commercial outcomes.",
  "Cover Production":
    "Translate the imprint lock into exact production decisions. Preserve family recognition, title-level distinction, legibility, safe areas, asset provenance, and confirmed dimensions.",
  "Interior Production":
    "Design for physical interaction, useful writing space, accessible hierarchy, clear page purpose, controlled repetition, and meaningful differences between titles.",
  "Print & Preflight":
    "Use only confirmed production specifications. Record the source and date of changeable dimensions, expose unresolved checks, and require a physical-proof decision before sign-off.",
  "Rights & Handoff":
    "Create traceable operational records without offering legal conclusions. Treat missing source, licence, approval, version, and responsibility evidence as a blocking gap.",
  "Listing & Metadata":
    "Align every field with the actual cover, interior, format, and audience. Treat keywords and categories as hypotheses until verified in the current marketplace.",
  "A+ Content":
    "Give each module one clear reader job. Keep copy factual, images original or cleared, comparisons accurate, and the complete storyboard within current module limits.",
  "Launch Strategy":
    "Build a dependency-led, claim-safe operating plan. Define assets, owners, dates, budget boundaries, stop conditions, and learning signals without predicting performance.",
  "Reader Funnel":
    "Lead with the promised reader resource, transparent consent, accurate delivery expectations, useful follow-up, and an optional next step that preserves reader choice.",
  "Cross-sell & Retention":
    "Recommend by remaining reader need and honest fit. Include exclusion logic, frequency boundaries, consent considerations, and evidence-led revision criteria.",
};

export function promptSupportsBookTarget(category: PromptCategory): boolean {
  return bookTargetCategories.has(category);
}

export function channelPromptText(
  text: string,
  project: PaperKingdomProject,
): string {
  if (getMarketplaceGuide(project.marketplace).usesKdpPrintSetup) return text;
  return text
    .replace(/\bback-cover\b/gi, "buyer-facing")
    .replace(/\bcover\b/gi, "listing preview")
    .replace(/\binterior\b/gi, "product-page")
    .replace(/\bbooks\b/gi, "products")
    .replace(/\bbook\b/gi, "product")
    .replace(/\breaders\b/gi, "customers")
    .replace(/\breader\b/gi, "customer");
}

export function getPromptReadiness(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput = project.promptCustomizations?.[prompt.id] ?? {
    customInstructions: "",
  },
): { missing: string[]; included: string[] } {
  const missing: string[] = [];
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  const itemNoun = isBook ? "book" : "product";
  const included = [
    project.marketplace,
    project.format,
    `${project.catalogue.length} ${itemNoun} ${project.catalogue.length === 1 ? "idea" : "ideas"}`,
  ];

  if (project.audience.trim()) included.push("Reader description");
  else missing.push(`Who the ${itemNoun} is for`);
  if (project.theme.trim()) included.push(`${isBook ? "Book" : "Product"} goal`);
  else missing.push(`What the ${itemNoun} should help with`);
  if (project.imprint.positioning.trim()) included.push("Optional publishing identity");

  const effectiveTarget =
    project.catalogue.find((book) => book.id === customization.targetBookId) ??
    (project.catalogue.length === 1 ? project.catalogue[0] : undefined);
  const booksInScope = effectiveTarget ? [effectiveTarget] : project.catalogue;
  const missingPurposes = booksInScope.filter((book) => !book.differentiator.trim());
  if (missingPurposes.length) {
    missing.push(`${missingPurposes.length} ${itemNoun} ${missingPurposes.length === 1 ? "purpose" : "purposes"}`);
  }
  const titleIsTheTask = ["Titles & Subtitles", "Catalogue Architecture"].includes(
    prompt.category,
  );
  const missingTitles = titleIsTheTask
    ? []
    : booksInScope.filter((book) => !book.title.trim());
  if (missingTitles.length) {
    missing.push(`${missingTitles.length} working ${missingTitles.length === 1 ? "title" : "titles"}`);
  }

  if (promptSupportsBookTarget(prompt.category)) {
    const target = effectiveTarget;
    included.push(
      target
        ? `${isBook ? "Book" : "Product"}: ${target.title || `${isBook ? "Book" : "Product"} ${target.sequence}`}`
        : `All ${itemNoun} ideas`,
    );
    if (
      target &&
      ["Listing & Metadata", "A+ Content"].includes(prompt.category) &&
      !project.production.interiorPlans[target.id]?.layoutIds.length
    ) {
      missing.push(`Inside-page plan for ${target.title || `${itemNoun} ${target.sequence}`}`);
    }
  }

  if (
    [
      "Keywords & Categories",
      "Listing & Metadata",
      "Launch Strategy",
    ].includes(prompt.category) &&
    !getChannelDelivery(project).trim()
  ) {
    missing.push(`Customer-file or delivery plan for ${project.marketplace}`);
  }

  if (prompt.category === "Reader Funnel") {
    included.push("Chosen optional resource");
    if (/QR insert|opt-in page/i.test(prompt.title)) {
      if (project.launchGrowth.qrDestination.trim()) included.push("Verified destination");
      else missing.push("Verified link or destination");
    }
  }

  if (prompt.category === "Cross-sell & Retention") {
    const otherNamedProducts = project.catalogue.filter(
      (book) => book.id !== effectiveTarget?.id && book.title.trim(),
    );
    if (otherNamedProducts.length) included.push("Other catalogue choices");
    else missing.push(`Another named ${itemNoun} with a distinct purpose`);
  }

  const interiorPlanCount = Object.values(project.production.interiorPlans).filter(
    (plan) => plan.layoutIds.length || plan.moduleIds.length || plan.sequenceNotes.trim(),
  ).length;
  if (interiorPlanCount) {
    included.push(`${interiorPlanCount} inside-page ${interiorPlanCount === 1 ? "plan" : "plans"}`);
  }
  if (customization.customInstructions?.trim()) included.push("Custom instructions");
  return { missing, included };
}

function buildProjectContext(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput,
): string {
  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const itemNoun = marketplaceGuide.usesKdpPrintSetup ? "book" : "product";
  const targetBook = project.catalogue.find(
    (book) => book.id === customization.targetBookId,
  ) ?? (project.catalogue.length === 1 ? project.catalogue[0] : undefined);
  const scopedBooks =
    targetBook &&
    !["Catalogue Architecture", "Titles & Subtitles"].includes(prompt.category)
      ? [targetBook]
      : project.catalogue;
  const detailedTitles = scopedBooks
    .map(
      (book) =>
        `${book.sequence}. ${book.title || "[working title needed]"}
   - Subtitle: ${book.subtitle || "[subtitle needed]"}
   - Distinct purpose: ${book.differentiator || "[define purpose]"}
   - Type and theme: ${book.type}; ${book.theme || "[theme needed]"}`,
    )
    .join("\n");

  const coverCategories = [
    "Cover Concepts",
    "Back-cover Copy",
    "Cover Production",
    "Print & Preflight",
    "Rights & Handoff",
  ];
  const interiorCategories = [
    "Interior Systems",
    "Product Descriptions",
    "Interior Production",
    "Print & Preflight",
    "Rights & Handoff",
    "Listing & Metadata",
    "A+ Content",
  ];
  const sellingCategories = [
    "Product Descriptions",
    "Keywords & Categories",
    "Promotion & Quality",
    "Listing & Metadata",
    "A+ Content",
    "Launch Strategy",
    "Reader Funnel",
    "Cross-sell & Retention",
  ];
  const includeCover = coverCategories.includes(prompt.category);
  const includeInterior = interiorCategories.includes(prompt.category);
  const includeSelling = sellingCategories.includes(prompt.category);
  const includeLaunch = [
    "Promotion & Quality",
    "A+ Content",
    "Launch Strategy",
    "Reader Funnel",
    "Cross-sell & Retention",
  ].includes(prompt.category);
  const activeCoverSystem = coverFamilySystems.find(
    (system) => system.id === project.production.coverSystemId,
  );
  const activeCoverPalette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const targetCover = targetBook
    ? project.production.coverPlans[targetBook.id]
    : undefined;
  const targetInterior = targetBook
    ? project.production.interiorPlans[targetBook.id]
    : undefined;
  const targetListing = targetBook
    ? getChannelListing(project, targetBook.id)
    : undefined;
  const layoutNames = (targetInterior?.layoutIds ?? []).map(
    (id) => interiorLayoutMasters.find((layout) => layout.id === id)?.name ?? id,
  );
  const moduleNames = (targetInterior?.moduleIds ?? []).map(
    (id) => interiorModules.find((module) => module.id === id)?.name ?? id,
  );
  const productionChannelContext = marketplaceGuide.usesKdpPrintSetup
    ? `- Print setup: ${project.production.trimSize}; ${project.production.paperType}; ${project.production.bleed}
- Barcode treatment: ${project.production.barcodeTreatment}`
    : `- Customer delivery or fulfilment: ${getChannelDelivery(project) || "[not yet recorded]"}
- Print-specific spine and barcode fields: not applicable unless the publisher separately chooses a print supplier`;
  const marketplaceExtras = marketplaceGuide.supportsAPlus
    ? `- Amazon A+ modules planned: ${project.launchGrowth.aPlusModules.length} of 5
- Amazon author page ready: ${project.launchGrowth.authorCentralComplete ? "yes" : "no"}`
    : `- No marketplace-specific extra content is required for this task.`;

  const approvedResults = getPromptWorkEntriesForChannel(project)
    .filter(
      ([key, work]) =>
        getPromptIdFromWorkKey(key) !== prompt.id &&
        work.status === "approved" &&
        work.useInLaterSteps &&
        work.response.trim(),
    )
    .map(([key, work]) => {
      const sourceId = getPromptIdFromWorkKey(key);
      const source = allPrompts.find((item) => item.id === sourceId);
      return `### ${source?.title ?? "Reviewed AI result"}\n${work.response.trim()}`;
    })
    .join("\n\n")
    .slice(0, 12000);

  const coverContext = includeCover
    ? `COVER WORK
- Chosen style: ${
        activeCoverSystem
          ? `${activeCoverSystem.name} — ${activeCoverSystem.character}`
          : "[not yet chosen]"
      }
- Chosen product colours: ${
        activeCoverPalette
          ? `${activeCoverPalette.name} — ${activeCoverPalette.colors
              .map((color) => `${color.name} ${color.hex}`)
              .join(", ")}`
          : "[not yet chosen]"
      }
- Important: these are product colours, not Paper Kingdom interface colours.
- Back-cover message: ${targetCover?.backCoverCopy || "[not yet written]"}
- Cover notes: ${project.production.coverNotes || "[none]"}`
    : "";
  const interiorContext = includeInterior
    ? `INSIDE-PAGE WORK
- Chosen layouts: ${layoutNames.join("; ") || "[not yet chosen]"}
- Reusable page types: ${moduleNames.join("; ") || "[not yet chosen]"}
- Page flow: ${targetInterior?.sequenceNotes || "[not yet described]"}`
    : "";
  const sellingContext = includeSelling
    ? `CURRENT ${marketplaceGuide.shortName.toUpperCase()} LISTING
- Buyer description: ${targetListing?.description || "[not yet written]"}
- Most likely buyer: ${targetListing?.audienceDetails || "[not yet written]"}
- Search ideas: ${targetListing?.keywords.filter(Boolean).join("; ") || "[not yet recorded]"}
- Categories or shop sections: ${targetListing?.categories.filter(Boolean).join("; ") || "[not yet confirmed]"}
- Live checks still noted: ${targetListing?.metadataNotes || "[none recorded]"}`
    : "";
  const launchContext = includeLaunch
    ? `CHANNEL-SPECIFIC OPTIONAL WORK
${marketplaceExtras}
- Launch approach: ${project.launchGrowth.evergreenPlanId}
- Launch date: ${project.launchGrowth.launchDate || "[not scheduled]"}
- Advertising objective: ${project.launchGrowth.adObjective || "[not yet recorded]"}
- Advertising budget boundary: ${project.launchGrowth.adBudgetNotes || "[not yet recorded]"}
- Launch notes: ${project.launchGrowth.launchNotes || "[none]"}
- Optional reader follow-up enabled: ${project.launchGrowth.funnelEnabled ? "yes" : "no"}
- Cross-sell paths selected: ${project.launchGrowth.crossSellTemplateIds.length}
- Follow-up notes: ${project.launchGrowth.funnelNotes || "[none]"}`
    : "";
  const customerFileContext =
    includeCover || includeInterior || includeSelling || includeLaunch
      ? `CUSTOMER FILES
${productionChannelContext}
- Typography specification: ${project.production.typographySpecification || "[not yet recorded]"}
- Rights items: ${project.production.rightsLedger.length}
- Proof records: ${project.production.proofLog.length}
- Revision notes: ${project.production.revisionNotes || "[none]"}`
      : "";

  return `${projectSummary(project)}

SALES CHANNEL IN PLAIN LANGUAGE
- Channel: ${marketplaceGuide.shortName}
- How it works: ${marketplaceGuide.plainDescription}
- First deliverable: ${marketplaceGuide.firstOutcome}
- Listing record: ${marketplaceGuide.listingName}

${marketplaceGuide.usesKdpPrintSetup ? "BOOK" : "PRODUCT"} RECORDS IN SCOPE
${detailedTitles || `[No ${itemNoun} names have been planned yet]`}

PROMPT SCOPE
- Category: ${prompt.category}
- Scope: ${
    targetBook
      ? `Title ${targetBook.sequence}: ${targetBook.title || "[working title needed]"}`
      : "Complete catalogue"
  }
${targetBook ? `- Target-title purpose: ${targetBook.differentiator || "[define purpose]"}` : ""}

PROJECT NOTES
${project.notes.trim() || "[No additional project notes]"}

${coverContext}

${interiorContext}

${customerFileContext}

${sellingContext}

${launchContext}

REVIEWED AI RESULTS FROM EARLIER STEPS
${approvedResults || "[No earlier AI result has been approved for reuse]"}`;
}

function buildPromptFocus(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput,
): string {
  const readiness = getPromptReadiness(prompt, project, customization);
  const missingDecisions = readiness.missing.length
    ? readiness.missing.map((item) => `- ${item}`).join("\n")
    : "- None. The supplied project context is ready for this task.";

  const structuredHandoff =
    prompt.category === "Titles & Subtitles"
      ? `## Paper Kingdom handoff
End the answer with these two plain lines so the publisher can preview and apply the result:
Recommended title: [one recommended title]
Recommended subtitle: [one recommended subtitle, or leave blank]`
      : prompt.category === "Interior Systems"
        ? `## Paper Kingdom handoff
Choose only names from the lists below, then end the answer with these four plain lines so the publisher can preview and apply the result:
Available layouts: ${interiorLayoutMasters.map((layout) => layout.name).join("; ")}
Available reusable page types: ${interiorModules.map((module) => module.name).join("; ")}

Paper Kingdom layouts: [semicolon-separated layout names]
Paper Kingdom page types: [semicolon-separated reusable page names]
Paper Kingdom page flow: [one concise sentence]
Paper Kingdom back-cover text: [one ready-to-use paragraph of no more than 90 words]`
        : "";

  return `## Assignment strategy
${categoryDirectives[prompt.category]}

## Assignment
${channelPromptText(prompt.objective, project)}

## Known context gaps
${missingDecisions}

${
  customization.customInstructions?.trim()
    ? `## Publisher instructions
${customization.customInstructions?.trim()}

`
    : ""
}

${structuredHandoff}`;
}

function compactText(value: string, maximum = 600): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maximum
    ? `${normalized.slice(0, maximum).trim()}...`
    : normalized;
}

type TitleLengthProfile = "short" | "medium" | "long";

const coverTitleSizeRanges: Record<
  (typeof coverFamilySystems)[number]["titleScale"],
  Record<TitleLengthProfile, string>
> = {
  restrained: { short: "34-46 pt", medium: "30-40 pt", long: "26-36 pt" },
  balanced: { short: "42-58 pt", medium: "34-48 pt", long: "28-40 pt" },
  large: { short: "48-68 pt", medium: "40-56 pt", long: "32-44 pt" },
  oversized: { short: "64-96 pt", medium: "52-76 pt", long: "40-60 pt" },
};

const formatTypographyRules: Record<
  PaperKingdomProject["format"],
  string
> = {
  "Low-content paperback":
    "Use clear 10.5-12.5 pt labels, strong 16-24 pt page headings, and generous writing space.",
  "Guided journal":
    "Use calm 10.5-12.5 pt guidance, 14-20 pt prompts, and enough leading and writing space for unhurried use.",
  "Activity book":
    "Use friendly 12-14 pt instructions, bold 18-28 pt activity headings, and unmistakable answer areas.",
  Workbook:
    "Use practical 10.5-12.5 pt instructions, 16-24 pt exercise headings, and a clear hierarchy between explanation and action.",
  Planner:
    "Use compact but comfortable 10.5-12 pt labels, 16-24 pt section headings, and high-contrast navigation that remains clear when printed.",
  "Printable PDF":
    "Use home-print-friendly 11-13 pt body text, 18-28 pt headings, and sufficient contrast in both colour and grayscale printing.",
};

function titleLengthProfile(title: string): {
  profile: TitleLengthProfile;
  words: number;
  characters: number;
} {
  const normalized = compactText(title || "Untitled", 180);
  const words = normalized.split(/\s+/).filter(Boolean).length;
  const characters = normalized.replace(/\s+/g, "").length;
  const profile: TitleLengthProfile =
    characters <= 24 && words <= 4
      ? "short"
      : characters <= 48 && words <= 8
        ? "medium"
        : "long";
  return { profile, words, characters };
}

function suggestedFontRoles(
  system: (typeof coverFamilySystems)[number] | undefined,
): string {
  if (!system) return "Choose one deliberate display and support pairing available to the current file tools.";
  const roles = [
    `display: ${system.fontSuggestions.display}`,
    `support: ${system.fontSuggestions.support}`,
  ];
  if (system.fontSuggestions.detail) {
    roles.push(`detail: ${system.fontSuggestions.detail}`);
  }
  return `${roles.join("; ")}. These are style references, not mandatory installed fonts. Use them when available; otherwise choose the closest available alternatives without asking the user.`;
}

function adaptiveCoverTypographyBrief(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const cover = book ? project.production.coverPlans[book.id] : undefined;
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const metrics = titleLengthProfile(book?.title || "Untitled book");
  const scale = system?.titleScale ?? "balanced";
  const titleRange = coverTitleSizeRanges[scale][metrics.profile];
  const backWords = (cover?.backCoverCopy || "").trim().split(/\s+/).filter(Boolean).length;
  const backRange = backWords > 180 ? "10.5-11.5 pt" : "11-13 pt";
  const titleTreatment = cover?.titleTreatment.trim()
    ? `Preserve this approved title treatment: ${compactText(cover.titleTreatment, 600)}`
    : `This is a ${metrics.profile} title (${metrics.words} words, ${metrics.characters} non-space characters). Start around ${titleRange} for an 8-10 inch print cover, then adjust proportionally to the actual template. Use deliberate line breaks instead of shrinking the title merely to keep one line.`;
  const subtitleTreatment = cover?.subtitleTreatment.trim()
    ? `Preserve this approved subtitle treatment: ${compactText(cover.subtitleTreatment, 600)}`
    : book?.subtitle.trim()
      ? "Keep the subtitle clearly secondary at roughly 35-50% of the title's visual scale, with comfortable spacing and no competition with the artwork."
      : "No subtitle is supplied; do not invent one or leave an empty subtitle placeholder.";

  return `Typography direction: ${system?.typeRule || "Choose a deliberate, highly legible type system suited to the product and reader."}
Preferred font roles: ${suggestedFontRoles(system)}
Lettering mood: ${compactText(project.imprint.typographyMood || system?.character || "Clear and appropriate for the reader", 240)}
Title hierarchy: ${titleTreatment}
Subtitle hierarchy: ${subtitleTreatment}
Back-cover typography: Keep the supplied wording readable at approximately ${backRange}, with 1.25-1.45 line spacing and a comfortable 45-70-character line length. Front, spine, and back must use one coordinated type system, not identical sizes.
Thumbnail test: At 10% of the finished front-cover size, the title must remain the first readable element and the subtitle must remain distinguishable.
Font handling: Use fonts available to the current file tools and record the actual font names used.`;
}

function adaptiveInteriorTypographyBrief(
  project: PaperKingdomProject,
): string {
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const functionalRule = project.production.typographySpecification.trim() ||
    formatTypographyRules[project.format];
  return `Display direction: ${system?.typeRule || "Use a clear display style appropriate to the reader and subject."}
Suggested display and support roles: ${suggestedFontRoles(system)}
Functional text: ${compactText(functionalRule, 900)}
Use the display direction for headings and navigation only; choose the support face for instructions, fields, and body copy. Use available embeddable fonts and record the actual font names used.`;
}

function practicalFileCreationContract(deliverable: string): string {
  return `## How to complete this file
Use the file-generation, coding, image, and PDF tools available in this chat to create the ${deliverable}. Make reasonable design decisions and work with the supplied files instead of returning advice.
- Inspect or calculate checks that the available tools can measure. Never say a check passed when it was not measured.
- If a preferred font is unavailable, use the closest available embeddable alternative and continue.
- If one non-critical production check cannot be measured, still create the most complete usable file possible. After the file link, add one short line beginning "Manual check:" that names only the unresolved item.
- Do not stop the whole task merely because a professional preflight tool is unavailable, and do not ask follow-up questions for non-critical choices.`;
}

function customerFileChannelRules(project: PaperKingdomProject): string {
  switch (project.marketplace) {
    case "Etsy":
      return `- Deliver a customer-facing file that works for home printing and on-screen use at the selected page size.
- Keep listing mock-ups, shop branding, print-cover marks, spine instructions, and barcode areas out of the customer PDF.
- Make the first page identify the product and its simple use without requiring a separate explanation from the seller.`;
    case "Creative Market":
      return `- Organize the ZIP with plainly named product files, a concise read-me, and a separate licence note; do not bury the usable PDF inside unnecessary folders.
- Keep preview graphics and marketplace listing copy separate from the customer product files.
- Do not introduce print-book trim, spine, barcode, or cover-template instructions.`;
    case "Shopify / direct":
      return project.format === "Low-content paperback"
        ? `- Build a printer-ready production package without assuming a marketplace-specific template, barcode, or fulfilment method.
- Keep the interior PDF, approved cover artwork, and production read-me as separate clearly named files.
- State the selected page size, bleed choice, and any printer-dependent item that still requires confirmation.`
        : `- Deliver the exact customer file promised by the product record, without store mock-ups or checkout copy inside it.
- Make the product self-explanatory after download and keep printing instructions short and channel-neutral.
- Do not introduce print-book trim, spine, barcode, or cover-template instructions.`;
    case "Multi-channel":
      return `- Build one clean source package and keep every channel-specific requirement in the channel-notes file rather than mixing it into the customer PDF.
- Do not treat one marketplace's cover, barcode, listing, or delivery rule as universal.
- Name each channel adaptation clearly and preserve one authoritative source file.`;
    case "Amazon KDP":
      return `- Keep the interior PDF separate from the cover preview and the final cover file created in a design tool.
- Use the selected trim, paper, bleed, page count, and current KDP preview evidence.
- Do not put template guides, barcode placeholders, or marketplace instructions inside the interior.`;
  }
}

function promptTargetBook(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput,
) {
  const selected = project.catalogue.find(
    (book) => book.id === customization.targetBookId,
  );
  if (selected) return selected;
  if (project.catalogue.length === 1 || promptSupportsBookTarget(prompt.category)) {
    return project.catalogue[0];
  }
  return undefined;
}

function buildCompactProjectFacts(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput,
): string {
  const guide = getMarketplaceGuide(project.marketplace);
  const targetBook = promptTargetBook(prompt, project, customization);
  const facts = [
    `Sales channel: ${guide.shortName}`,
    `How that channel works: ${compactText(guide.plainDescription, 260)}`,
    `Format: ${project.format}`,
    `Reader: ${compactText(project.audience || "Not provided", 300)}`,
    `Product goal: ${compactText(project.theme || "Not provided", 400)}`,
  ];

  if (targetBook) {
    facts.push(
      `Working title: ${compactText(targetBook.title || "Not chosen yet", 180)}`,
      `Product purpose: ${compactText(targetBook.differentiator || "Not provided", 400)}`,
    );
    if (targetBook.subtitle.trim()) {
      facts.push(`Current subtitle: ${compactText(targetBook.subtitle, 240)}`);
    }
  } else if (project.catalogue.length) {
    const catalogue = project.catalogue
      .slice(0, 8)
      .map(
        (book) =>
          `${book.sequence}. ${compactText(book.title || "Untitled", 100)} — ${compactText(
            book.differentiator || "Purpose not provided",
            180,
          )}`,
      )
      .join("; ");
    facts.push(`Products in scope: ${catalogue}`);
  }

  if (project.notes.trim()) {
    facts.push(`Important boundaries or notes: ${compactText(project.notes, 700)}`);
  }

  if (project.imprint.name.trim() && project.imprint.name !== "My Publishing Name") {
    facts.push(`Publishing name: ${compactText(project.imprint.name, 120)}`);
  }

  const includeCover = [
    "Cover Concepts",
    "Back-cover Copy",
    "Cover Production",
    "Print & Preflight",
  ].includes(prompt.category);
  if (includeCover) {
    const system = coverFamilySystems.find(
      (item) => item.id === project.production.coverSystemId,
    );
    const palette = resolveCoverPalette(
      project.production.coverPaletteId,
      project.production.customCoverPalette,
    );
    if (system) {
      facts.push(
        `Chosen visual style: ${system.name} — ${system.character}`,
        `Chosen composition system: ${system.grid}`,
        `Chosen typography direction: ${system.typeRule}`,
        `Suggested font roles: ${suggestedFontRoles(system)}`,
      );
    }
    if (palette) {
      facts.push(
        `Chosen product colours: ${palette.name} — ${palette.colors
          .map((color) => `${color.name} ${color.hex}`)
          .join(", ")}`,
      );
    }
    if (targetBook) {
      const cover = project.production.coverPlans[targetBook.id];
      if (cover?.titleTreatment.trim()) {
        facts.push(`Approved title treatment: ${compactText(cover.titleTreatment, 500)}`);
      }
      if (cover?.subtitleTreatment.trim()) {
        facts.push(`Approved subtitle treatment: ${compactText(cover.subtitleTreatment, 500)}`);
      }
      if (cover?.backCoverCopy.trim()) {
        facts.push(`Back-cover message: ${compactText(cover.backCoverCopy, 500)}`);
      }
    }
    if (project.imprint.typographyMood.trim()) {
      facts.push(`Publishing lettering mood: ${compactText(project.imprint.typographyMood, 240)}`);
    }
    if (project.imprint.coverRules.trim()) {
      facts.push(`Publishing cover rules: ${compactText(project.imprint.coverRules, 500)}`);
    }
    if (project.production.coverNotes.trim()) {
      facts.push(`Cover notes: ${compactText(project.production.coverNotes, 500)}`);
    }
  }

  const includeInterior = [
    "Interior Systems",
    "Back-cover Copy",
    "Product Descriptions",
    "Promotion & Quality",
    "Interior Production",
    "Listing & Metadata",
    "A+ Content",
  ].includes(prompt.category);
  if (includeInterior && targetBook) {
    const interior = project.production.interiorPlans[targetBook.id];
    if (interior?.layoutIds.length) {
      facts.push(
        `Chosen page layouts: ${interior.layoutIds
          .map(
            (id) =>
              interiorLayoutMasters.find((layout) => layout.id === id)?.name ?? id,
          )
          .join(", ")}`,
      );
    }
    if (interior?.moduleIds.length) {
      facts.push(
        `Chosen reusable page types: ${interior.moduleIds
          .map(
            (id) => interiorModules.find((module) => module.id === id)?.name ?? id,
          )
          .join(", ")}`,
      );
    }
    if (interior?.sequenceNotes.trim()) {
      facts.push(`Current page flow: ${compactText(interior.sequenceNotes, 500)}`);
    }
  }

  if (
    ["Cover Production", "Interior Production", "Print & Preflight", "Rights & Handoff"].includes(
      prompt.category,
    )
  ) {
    if (guide.usesKdpPrintSetup) {
      facts.push(
        `Print setup: ${project.production.trimSize}; ${project.production.paperType}; ${project.production.bleed}`,
      );
    } else {
      facts.push(
        `Customer delivery: ${compactText(getChannelDelivery(project) || "Not provided", 500)}`,
      );
    }
  }

  const sellingCategories: PromptCategory[] = [
    "Product Descriptions",
    "Keywords & Categories",
    "Promotion & Quality",
    "Listing & Metadata",
    "A+ Content",
    "Launch Strategy",
    "Reader Funnel",
    "Cross-sell & Retention",
  ];
  if (sellingCategories.includes(prompt.category) && targetBook) {
    const listing = getChannelListing(project, targetBook.id);
    if (listing?.description.trim()) {
      facts.push(`Current buyer description: ${compactText(listing.description, 600)}`);
    }
    if (listing?.audienceDetails.trim()) {
      facts.push(`Current buyer details: ${compactText(listing.audienceDetails, 300)}`);
    }
    const keywords = listing?.keywords.filter((keyword) => keyword.trim()) ?? [];
    if (keywords.length) facts.push(`Current search phrases: ${keywords.join("; ")}`);
    const categories = listing?.categories.filter((category) => category.trim()) ?? [];
    if (categories.length) facts.push(`Current category ideas: ${categories.join("; ")}`);
    if (listing?.metadataNotes.trim()) {
      facts.push(`Unresolved listing checks: ${compactText(listing.metadataNotes, 400)}`);
    }
  }

  if (["A+ Content", "Launch Strategy", "Reader Funnel", "Cross-sell & Retention"].includes(prompt.category)) {
    const launchPlan = evergreenLaunchPlans.find(
      (plan) => plan.id === project.launchGrowth.evergreenPlanId,
    );
    if (launchPlan) {
      facts.push(`Chosen launch approach: ${launchPlan.name} - ${launchPlan.purpose}`);
    }
    if (project.launchGrowth.launchDate.trim()) {
      facts.push(`Planned launch date: ${project.launchGrowth.launchDate}`);
    }
    if (project.launchGrowth.launchNotes.trim()) {
      facts.push(`Launch notes: ${compactText(project.launchGrowth.launchNotes, 500)}`);
    }
    facts.push(
      `Amazon author page ready: ${project.launchGrowth.authorCentralComplete ? "Yes" : "No"}`,
    );
    if (project.launchGrowth.adObjective.trim()) {
      facts.push(`Advertising objective: ${compactText(project.launchGrowth.adObjective, 300)}`);
    }
    if (project.launchGrowth.adBudgetNotes.trim()) {
      facts.push(`Advertising budget boundary: ${compactText(project.launchGrowth.adBudgetNotes, 300)}`);
    }
  }

  if (prompt.category === "A+ Content" && project.launchGrowth.aPlusModules.length) {
    facts.push(
      `Existing A+ modules: ${project.launchGrowth.aPlusModules
        .map((module) => module.headline || module.storyboardId)
        .join("; ")}`,
    );
  }

  if (prompt.category === "A+ Content") {
    facts.push(
      `Other named catalogue products: ${project.catalogue
        .filter((book) => book.id !== targetBook?.id && book.title.trim())
        .map((book) => `${book.title} - ${book.differentiator || "Purpose not provided"}`)
        .join("; ") || "None recorded"}`,
    );
  }

  if (prompt.category === "Reader Funnel") {
    const leadMagnet = leadMagnetSystems.find(
      (item) => item.id === project.launchGrowth.leadMagnetId,
    );
    const welcome = welcomeSequences.find(
      (item) => item.id === project.launchGrowth.welcomeSequenceId,
    );
    if (leadMagnet) {
      facts.push(
        `Chosen optional resource: ${leadMagnet.name} - ${leadMagnet.promise}; contents: ${leadMagnet.structure}`,
      );
    }
    facts.push(
      `Reader follow-up enabled: ${project.launchGrowth.funnelEnabled ? "Yes" : "No"}`,
      `QR destination: ${project.launchGrowth.qrDestination.trim() || "Not provided"}`,
    );
    if (welcome) facts.push(`Chosen email sequence: ${welcome.name} - ${welcome.emails.join("; ")}`);
    if (project.launchGrowth.insertCopy.trim()) {
      facts.push(`Current insert copy: ${compactText(project.launchGrowth.insertCopy, 400)}`);
    }
    if (project.launchGrowth.optInHeadline.trim() || project.launchGrowth.optInBody.trim()) {
      facts.push(
        `Current sign-up page: ${compactText(
          `${project.launchGrowth.optInHeadline} ${project.launchGrowth.optInBody}`,
          600,
        )}`,
      );
    }
    if (project.launchGrowth.funnelNotes.trim()) {
      facts.push(`Reader follow-up notes: ${compactText(project.launchGrowth.funnelNotes, 500)}`);
    }
  }

  if (prompt.category === "Cross-sell & Retention") {
    const selectedRoutes = project.launchGrowth.crossSellTemplateIds
      .map((id) => crossSellTemplates.find((item) => item.id === id))
      .filter((item): item is (typeof crossSellTemplates)[number] => Boolean(item));
    facts.push(
      `Other catalogue choices: ${project.catalogue
        .filter((book) => book.id !== targetBook?.id && book.title.trim())
        .map((book) => `${book.title} - ${book.differentiator || "Purpose not provided"}`)
        .join("; ") || "None recorded"}`,
    );
    if (selectedRoutes.length) {
      facts.push(
        `Chosen recommendation routes: ${selectedRoutes
          .map((route) => `${route.name} - ${route.purpose}`)
          .join("; ")}`,
      );
    }
  }

  return facts.map((fact) => `- ${fact}`).join("\n");
}

function compactApprovedResults(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
): string {
  return getPromptWorkEntriesForChannel(project)
    .filter(
      ([key, work]) => {
        const source = allPrompts.find(
          (item) => item.id === getPromptIdFromWorkKey(key),
        );
        return Boolean(
          source &&
            source.number < prompt.number &&
            work.status === "approved" &&
            work.useInLaterSteps &&
            work.response.trim(),
        );
      },
    )
    .map(([key, work]) => {
      const source = allPrompts.find(
        (item) => item.id === getPromptIdFromWorkKey(key),
      );
      if (source?.category === "Titles & Subtitles") {
        const result = extractTitleRecommendation(work.response);
        if (result.title) {
          return `${source.title}: ${result.title}${
            result.subtitle ? ` — ${result.subtitle}` : ""
          }`;
        }
      }
      if (source?.category === "Interior Systems") {
        const result = extractInteriorRecommendation(work.response);
        const layouts = result.layoutIds
          .map(
            (id) =>
              interiorLayoutMasters.find((layout) => layout.id === id)?.name ?? id,
          )
          .join(", ");
        if (layouts || result.sequenceNotes) {
          return `${source.title}: ${[layouts, result.sequenceNotes]
            .filter(Boolean)
            .join(" — ")}`;
        }
      }
      return `${source?.title ?? "Earlier approved answer"}: ${compactText(
        work.response,
        700,
      )}`;
    })
    .join("\n")
    .slice(0, 2400);
}

const compactWordLimits: Record<PromptCategory, number> = {
  "Imprint Strategy": 350,
  "Market Intelligence": 450,
  "Catalogue Architecture": 450,
  "Titles & Subtitles": 220,
  "Cover Concepts": 300,
  "Interior Systems": 500,
  "Back-cover Copy": 300,
  "Product Descriptions": 350,
  "Keywords & Categories": 350,
  "Promotion & Quality": 450,
  "Cover Production": 450,
  "Interior Production": 500,
  "Print & Preflight": 400,
  "Rights & Handoff": 400,
  "Listing & Metadata": 400,
  "A+ Content": 400,
  "Launch Strategy": 450,
  "Reader Funnel": 350,
  "Cross-sell & Retention": 350,
};

function immediateTask(prompt: PromptDefinition, project: PaperKingdomProject): string {
  return channelPromptText(prompt.objective, project);
}

function composeCoverArtworkPrompt(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput,
): string {
  const book = promptTargetBook(prompt, project, customization);
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const palette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const visualRequest = compactText(
    customization.customInstructions ||
      (book ? project.production.coverPlans[book.id]?.focalBrief : "") ||
      "Make it simple, practical, trustworthy, and easy to understand.",
    600,
  );
  const lines = [
    `Create a new portrait illustration for the cover of “${
      book?.title || "this book"
    }”.`,
    `It is for ${compactText(project.audience || "the intended reader", 260)}.`,
    `The book helps them ${compactText(project.theme || book?.differentiator || "use the book", 300)}.`,
    `Visual direction: ${visualRequest}`,
  ];

  if (system) {
    lines.push(
      `Style: ${system.name} — ${system.character}.`,
      `Composition: ${system.grid}.`,
      `Subject treatment: ${system.focalRule}.`,
    );
  }
  if (palette) {
    lines.push(
      `Colours: ${palette.colors
        .map((color) => `${color.name} ${color.hex}`)
        .join(", ")}.`,
    );
  }

  lines.push(
    "Leave a large clear area at the top for the title to be added later.",
    "Do not include words, letters, logos, borders, barcodes, or a book mockup.",
  );

  return lines.join("\n\n");
}

function compactInteriorChoices(project: PaperKingdomProject): {
  layouts: string;
  modules: string;
} {
  const layoutIds = [
    ...recommendedLayoutIds(project.format),
    "inventory",
    "log",
    "checklist",
    "conversation",
    "closing-review",
  ];
  const layouts = [...new Set(layoutIds)]
    .map(
      (id) => interiorLayoutMasters.find((layout) => layout.id === id)?.name,
    )
    .filter((name): name is string => Boolean(name))
    .slice(0, 10);

  const preferredModuleNames = [
    ...recommendedModuleIds(project.format)
      .map((id) => interiorModules.find((module) => module.id === id)?.name)
      .filter((name): name is string => Boolean(name)),
    "Observation Record",
    "Conversation Prep",
    "Appointment Brief",
    "Resource Log",
    "Completion Review",
    "Home Reset Checklist",
  ];
  const availableNames = new Set(interiorModules.map((module) => module.name));
  const modules = [...new Set(preferredModuleNames)]
    .filter((name) => availableNames.has(name))
    .slice(0, 10);

  return { layouts: layouts.join("; "), modules: modules.join("; ") };
}

function compactOutputInstructions(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
): string {
  if (prompt.id === "pk-title-01") {
    return `Return only:
1. Five short title and subtitle options.
2. One recommended option with a one-sentence reason.
3. These exact final lines:
Recommended title: [title]
Recommended subtitle: [subtitle]

Keep the complete answer under 180 words. Do not add scoring tables, a project recap, production advice, or next steps.`;
  }

  if (prompt.id === "pk-interior-01") {
    const choices = compactInteriorChoices(project);
    return `Return only:
1. The smallest useful ordered page plan, normally three to five page types.
2. The exact fields or questions on each page type.
3. A short explanation of which pages repeat.
4. One ready-to-use back-cover paragraph that says who the product is for, what is inside, and what it helps them do without promises or hype.
5. These exact final lines using only names from the available lists:

Before finishing:
- Do not choose the same function as both a layout and a reusable page type.
- Make every repeating page match how often the reader will actually use it.
- Merge closely related pages when separating them would add unnecessary work.
- Do not add a daily page, weekly review, tracker, checklist, or closing review merely because it is available. Include it only when the project facts require that cadence or function.
- For a simple record or reference product, prefer one repeating main page with only the essential supporting pages.

Available layouts: ${choices.layouts}
Available reusable page types: ${choices.modules}
Paper Kingdom layouts: [semicolon-separated layout names]
Paper Kingdom page types: [semicolon-separated reusable page names]
Paper Kingdom page flow: [one concise sentence]
Paper Kingdom back-cover text: [one ready-to-use paragraph of no more than 90 words]

Keep the complete answer under 500 words. Do not add a project recap, general publishing advice, or next steps.`;
  }

  if (prompt.id === "pk-description-01") {
    return `Return only:
1. One ready-to-use product description.
2. Five short, factual feature bullets.
3. One sentence explaining who it is for and one sentence explaining what it is not.

Keep the complete answer under 350 words. Do not add analysis, a project recap, or next steps.`;
  }

  const title = prompt.title.toLowerCase();
  const finish = `Keep the complete answer under ${compactWordLimits[prompt.category]} words. Do not ask questions, repeat the project brief, or add extra tasks.`;
  const isReviewTask =
    /audit|critique|preflight|proof inspection|review$|check$|assessment$|recognition test|stress test|differentiation test|quality gate|readiness gate|sign-off|naturalness edit|length edit|clarity pass/.test(
      title,
    );

  if (title === "image-model master prompt") {
    return `Return only one plain, ready-to-paste image-generation prompt of no more than 160 words. Include the subject, composition, visual style, palette, lighting, a large empty title area, portrait orientation, and exclusions. Do not include commentary before or after the prompt.

Do not request words inside the image, editable layers, vector files, CMYK output, exact print dimensions, or other features an image generator cannot reliably deliver. ${finish}`;
  }

  if (title === "cover variation prompts") {
    return `Return only six numbered, ready-to-paste image-generation prompts. Keep the shared art direction identical and change only the title-specific subject. Put all exclusions inside each prompt. Do not add explanations.

${finish}`;
  }

  if (title === "qr insert copy") {
    return `Return only the final in-book insert:
1. One short headline.
2. Two concise sentences explaining the chosen optional resource and its exact reader benefit.
3. One voluntary call-to-action line.
4. The supplied destination and one plain privacy-expectation sentence.

Do not invent a URL, resource, delivery method, data practice, or benefit. If the destination is marked "Not provided", use the exact line "Status: Not ready until a verified destination is added" instead of fabricating final copy. ${finish}`;
  }

  if (prompt.category === "Market Intelligence") {
    return `Return only:
1. The requested research table, map, or worksheet already filled with the best conclusions supported by the project facts.
2. Label each important point as Supported, Hypothesis, or Needs live verification.
3. For anything unverified, give the exact marketplace check and evidence to record; never invent demand, search volume, prices, or competitor facts.
4. One best current decision based on the available evidence.

Do not treat result counts, competitor counts, review counts, or the absence of competitors as proof of demand. Base proceed, revise, or pause thresholds on evidence quality, reader-problem fit, and product distinctness instead of arbitrary numbers.

${finish}`;
  }

  if (prompt.category === "Print & Preflight") {
    return `Return only a strict production table with: required check | evidence currently supplied | Pass, Revise, Stop, or Not verified | exact correction.

Cover every relevant release check: trim or page size, page count, margins and gutter, bleed, blank pages, fonts, images and line quality, cover dimensions and spine, barcode area, rights, PDF export, platform preview, and physical proof. Do not mark a project fact as "Pass" unless file, preview, or proof evidence confirms the actual production check. End with one final release decision.

Never infer changing platform specifications. ${finish}`;
  }

  if (prompt.category === "Rights & Handoff") {
    return `Return only the completed operational record requested in the task. Use a copy-ready table with asset or file, source, owner, version, evidence, restrictions, approval status, and exact unresolved item. Include a final handoff or archive checklist.

Do not give legal conclusions, invent a source or licence, or leave blank questions for the user to interpret. ${finish}`;
  }

  if (prompt.category === "Listing & Metadata") {
    return `Return only the requested listing fields, copy, research table, or sign-off record in the order they are entered on the marketplace. Make every completed field ready to paste. Mark unverified keywords, categories, and changing platform details clearly instead of inventing them.

End with a compact "Approved fields / Not verified" split. ${finish}`;
  }

  if (isReviewTask) {
    return `Return only:
1. A compact table with: item checked | pass or issue | exact correction.
2. The corrected wording, structure, or specification wherever a correction can be made now.
3. A final verdict: Pass, Revise, or Stop, followed by one sentence explaining why.

Judge only the facts supplied. If evidence is unavailable, mark it "Not verified" instead of inventing it. ${finish}`;
  }

  switch (prompt.category) {
    case "Imprint Strategy":
      return `Return only:
1. The finished brand decision or asset named in the task.
2. Ready-to-use wording and concrete examples, not general branding advice.
3. A short "Use / Avoid" list.
4. One clear recommended direction.

${finish}`;

    case "Catalogue Architecture":
      return `Return only:
1. The completed catalogue matrix, sequence, or decision requested in the task.
2. Give every proposed title or product a distinct reader job and use case.
3. Show any overlap or conflict beside its exact correction.
4. End with the recommended catalogue structure in one compact list.

${finish}`;

    case "Titles & Subtitles":
      return `Return only:
1. The exact title artifact named in the Task and no adjacent title work.
2. If the Task asks for options, give eight concise options. Add subtitles, scores, hierarchy, or rewrites only when the Task explicitly asks for them.
3. Keep every option short enough to work on a cover and natural enough to say aloud. Flag only genuine confusion or verification risks.
4. End with one recommended result and a one-sentence reason.

${finish}`;

    case "Cover Concepts":
      return `Return only:
1. The finished cover direction or system requested in the task.
2. Specify focal subject, composition, title area, hierarchy, palette, typography character, and the elements that vary across titles.
3. Include one short ready-to-paste image prompt when new artwork is required.
4. End with one recommended direction and its main print risk.

Do not create a book mockup or place generated words inside artwork. ${finish}`;

    case "Interior Systems":
      return `Return only:
1. The complete page, spread, module, or sequence requested in the task.
2. Write the exact headings, instructions, fields, questions, and answer choices the reader will see.
3. State what repeats, how often it repeats, and what changes between repetitions.
4. Include one filled example where it makes the page easier to understand.

Before returning the answer, silently check that every answer choice matches its question, no two fields do the same job, and no question asks the reader to diagnose a problem or claim expertise. Do not merely describe what a designer could make. Produce the usable page content now. ${finish}`;

    case "Back-cover Copy":
      return `Return only the final, ready-to-place back cover:
1. Headline.
2. Main copy.
3. Three to five factual benefit bullets when space allows.
4. One concise ideal-reader line.

Use only contents and benefits explicitly named in the project facts. Do not infer common journal sections, tracking features, page types, or a start-to-finish process. Do not add analysis or explain the writing choices. ${finish}`;

    case "Product Descriptions":
      return `Return only the final listing content requested in the task, ready to paste:
1. Main description with short, scannable paragraphs.
2. Five factual feature bullets unless the task asks for a shorter format.
3. A brief ideal-fit statement and an honest expectation-setting statement.

Mention only contents explicitly named in the project facts; do not fill gaps with common product features. Do not add analysis, placeholders, or explain the writing choices. ${finish}`;

    case "Keywords & Categories":
      return `Return only the requested keyword or category research artifact:
1. Put the three strongest natural phrases first.
2. For each phrase show reader intent, why it fits this exact product, where it may be used, and verification status.
3. Separate confirmed project language from hypotheses that require a live marketplace check.
4. End with a copy-ready shortlist; never invent search volume or category availability.

${finish}`;

    case "Promotion & Quality":
      return `Return only the finished campaign asset, script, sequence, checklist, or review requested in the task. Write all customer-facing copy in full, place actions in usable order, and attach a clear owner or completion check where operations are involved.

Do not return a menu of possibilities or general marketing advice. ${finish}`;

    case "Cover Production":
      return `Return only the production-ready cover artifact requested in the task:
1. Exact front, spine, and back rules that apply.
2. Fixed family elements and title-level variables.
3. Required assets, dimensions that still need verification, and pass/fail checks.
4. A concise handoff a designer can follow without further interpretation.

${finish}`;

    case "Interior Production":
      return `Return only the implementation-ready interior artifact requested in the task. Include the exact page order or master-page mapping, visible headings and fields, repetition rules, accessibility checks, and title-specific variations.

Do not stop at recommendations; supply the concrete page or system specification. ${finish}`;

    case "A+ Content":
      return `Return only the finished A+ module or storyboard requested in the task. For each module give its job, final headline, final body copy, image composition, alt-text direction, and the factual product detail it proves. Limit the full storyboard to five modules.

Use only page types, catalogue products, and contents explicitly named in the project facts. If no other catalogue product is recorded, do not invent comparison content or call the product part of a series. Do not return general A+ advice or invent page images, dimensions, eligibility, or claims. ${finish}`;

    case "Launch Strategy":
      return `Return only the usable launch asset or plan requested in the task. For a plan, use a compact table with timing, action, finished asset, owner, dependency, completion check, and stop or revise condition. Write any required promotional copy in full.

Do not predict sales or add optional ideas after the finished deliverable. ${finish}`;

    case "Reader Funnel":
      return `Return only the finished funnel asset requested in the task. Write every reader-facing headline, paragraph, email, button, consent line, and delivery instruction in full. For a sequence, show the exact order, trigger, and destination.

Keep the path useful and voluntary; do not return funnel theory, placeholders, or extra upsell ideas. ${finish}`;

    case "Cross-sell & Retention":
      return `Return only the finished routing map, copy, sequence, or evidence plan requested in the task. Connect each recommendation to a specific remaining reader need, include when not to recommend it, and write all customer-facing copy in full.

Recommend only catalogue products explicitly named in the project facts. If none fit, say "No relevant next product recorded" rather than inventing one. Do not use pressure, artificial urgency, generic retention advice, or an offer to continue. ${finish}`;
  }
}

export function composePrompt(
  prompt: PromptDefinition,
  project: PaperKingdomProject,
  customization: PromptCustomizationInput = project.promptCustomizations?.[prompt.id] ?? {
    customInstructions: "",
  },
): string {
  if (prompt.id === "pk-cover-01") {
    return composeCoverArtworkPrompt(prompt, project, customization);
  }

  const approvedResults = compactApprovedResults(prompt, project);
  const publisherRequest = customization.customInstructions?.trim()
    ? `\n## Your additional request\n${compactText(customization.customInstructions, 1200)}\n`
    : "";
  const earlierDecisions = approvedResults
    ? `\n## Earlier decisions to preserve\n${approvedResults}\n`
    : "";

  return `# Complete this task now

Do not ask what I want you to do and do not summarize this brief. Perform the task below immediately. Use a sensible assumption for a missing non-critical detail and complete the task without asking questions. If a missing fact cannot be responsibly inferred, mark it "Not verified" and finish everything else.

## Task
${immediateTask(prompt, project)}

## Project facts
${buildCompactProjectFacts(prompt, project, customization)}
${publisherRequest}${earlierDecisions}
## Accuracy rules
- Use the project facts as the source of truth. Do not invent research, rights, marketplace approval, performance, or guaranteed outcomes.
- Follow the stated audience, purpose, format, and boundaries. Keep the result practical and easy to use.

## Output
${compactOutputInstructions(prompt, project)}`;
}

export function composeSellingKitPrompt(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const blueprint = buildAdaptivePageBlueprint(project, bookId);
  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const sellingGuide = getSellingKitGuide(project.marketplace);
  const coverSystem = coverFamilySystems.find(
    (system) => system.id === project.production.coverSystemId,
  );
  const palette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const paletteColors = palette?.colors ?? project.production.customCoverPalette.colors;
  const titleRule = sellingGuide.titleEditable
    ? "Write a clear buyer-facing listing title for this sales channel."
    : `Use this exact title: ${book?.title || "Untitled product"}.`;
  const seoRule = sellingGuide.showSeoFields
    ? "Write a concise search-engine title and description for the product page."
    : "Return empty strings for seoTitle and seoDescription.";

  return `Create the complete ${marketplaceGuide.shortName} selling kit for this product now. Do not ask questions or offer choices.

Sales channel: ${marketplaceGuide.shortName}
Product: ${book?.title || "Untitled product"}
Subtitle: ${book?.subtitle || "None"}
Format: ${project.format}
Reader: ${compactText(project.audience || "Not provided", 320)}
Reader outcome: ${compactText(project.theme || "Not provided", 420)}
Product purpose: ${compactText(book?.differentiator || "Not provided", 420)}
Contents: ${blueprint.countSummary}
Finished customer files: ${compactText(getChannelDelivery(project) || "Not yet confirmed", 300)}
Visual direction: ${coverSystem?.name || "Use the existing product design"}
Colours: ${paletteColors.map((color) => `${color.name} ${color.hex}`).join(", ")}
Publisher notes: ${compactText(project.notes || "None", 500)}

Create:
- one accurate listing title; ${titleRule}
- one buyer-facing description; ${sellingGuide.descriptionFocus}
- one concise ideal-buyer description;
- exactly ${sellingGuide.searchCount} distinct ${sellingGuide.searchLabel.toLowerCase()};
- one short pitch, three genuinely different social posts, and one useful launch email;
- exactly ${sellingGuide.visualCount} practical items for the ${sellingGuide.visualLabel.toLowerCase()}; ${sellingGuide.visualHint}
- ${seoRule}

Return only valid JSON using these exact keys:
{
  "listingTitle": "",
  "description": "",
  "idealBuyer": "",
  "searchTerms": [""],
  "seoTitle": "",
  "seoDescription": "",
  "shortPitch": "",
  "socialPosts": ["", "", ""],
  "launchEmailSubject": "",
  "launchEmailBody": "",
  "visualPlan": [""]
}

Use only the supplied facts. Do not invent files, features, reviews, demand, rankings, results, rights, or guarantees. Do not add categories because the user must choose from the current live account. Do not include markdown or commentary outside the JSON.`;
}

export function composeSimpleListingDraftPrompt(
  project: PaperKingdomProject,
  bookId: string,
): string {
  return composeSellingKitPrompt(project, bookId);
}

function promptFilename(value: string): string {
  return value
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, " ")
    .trim() || "Paper Kingdom book";
}

function minimumKdpGutter(pageCount: number): string {
  if (pageCount <= 150) return "0.375 in";
  if (pageCount <= 300) return "0.5 in";
  if (pageCount <= 500) return "0.625 in";
  if (pageCount <= 700) return "0.75 in";
  return "0.875 in";
}

function adaptivePageSpecifications(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const blueprint = buildAdaptivePageBlueprint(project, bookId);
  return blueprint.entries
    .map(
      (entry) =>
        `- ${entry.name} — ${entry.count} page${entry.count === 1 ? "" : "s"}. Purpose: ${entry.purpose}. Required structure: ${entry.structure}.`,
    )
    .join("\n");
}

export function composeInteriorPdfCreationPrompt(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const plan = book ? project.production.interiorPlans[book.id] : undefined;
  const blueprint = buildAdaptivePageBlueprint(project, bookId);
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const palette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const approvedPlan = getPromptWorkForChannel(project, "pk-interior-01")?.response.trim();
  const filename = `${promptFilename(book?.title || project.name)} - inside.pdf`;
  const usesColour = /colou?r/i.test(project.production.paperType);
  const outsideMargin = project.production.bleed === "Bleed" ? "0.375 in" : "0.25 in";

  return `Create the complete finished print-ready interior PDF now. Do not return a plan, instructions, code, or sample pages. Generate the actual file.

## Product selected in the app
Sales channel: ${project.marketplace}
Format: ${project.format}
Book: ${book?.title || "Untitled book"}
Subtitle: ${book?.subtitle || "None"}
Reader: ${compactText(project.audience || "Not provided", 320)}
Reader outcome: ${compactText(project.theme || "Not provided", 420)}
Book purpose: ${compactText(book?.differentiator || "Not provided", 420)}
Subject: ${compactText(book?.theme || project.theme || "Not provided", 240)}
Publisher notes: ${compactText(project.notes || "None", 700)}

## Finished file
File name: ${filename}
Page size: ${project.production.trimSize}
Page count: exactly ${blueprint.targetPageCount}
Bleed: ${project.production.bleed === "Bleed" ? "full bleed; extend backgrounds and edge artwork by 0.125 in beyond the top, bottom, and outside trim edges" : "no bleed; the PDF page size must equal the finished trim size"}
Paper: ${project.production.paperType}
Binding-safe margins: mirror left and right pages; use an inside gutter of at least ${minimumKdpGutter(blueprint.targetPageCount)} and an outside margin of at least ${outsideMargin}

## Product design already chosen
Visual style: ${system ? `${system.name} — ${system.character}` : "Clear and appropriate for the reader and subject"}
Product colours: ${palette?.colors.map((color) => `${color.name} ${color.hex}`).join("; ") || "Not selected"}
Interior colour use: ${usesColour ? "Use the selected palette with accessible contrast." : "Design for black-and-white printing. Translate the palette into a restrained grayscale hierarchy and never rely on colour to communicate meaning."}
Interior consistency rules: ${compactText(project.imprint.interiorRules || "Keep margins, fields, headings, line spacing, and navigation consistent.", 900)}
Voice rules: ${compactText(project.imprint.voiceRules || "Use plain, encouraging language suited to the reader.", 700)}

## Adaptive typography
${adaptiveInteriorTypographyBrief(project)}

${practicalFileCreationContract("finished interior PDF")}

## Mandatory page allocation
${blueprint.countSummary}

## Mandatory page specifications
${adaptivePageSpecifications(project, bookId)}

## Mandatory reading order
Publisher's earlier flow preference: ${compactText(plan?.sequenceNotes || "None recorded", 700)}
Calculated complete flow: ${blueprint.sequenceSummary}

Follow this exact page manifest. Do not replace the calculated allocation with one dominant repeated page:
${blueprint.manifest}

${approvedPlan ? `## Approved content details to preserve\n${compactText(approvedPlan, 4200)}\n` : ""}
## Production rules
- Create every page in the manifest, including all reader-facing labels, short instructions, writing fields, checkboxes, and page numbers.
- Repeating pages must be genuinely useful at their assigned cadence. Keep the core template consistent, but place it in the calculated monthly, weekly, or section structure.
- Do not add placeholder text, invented claims, generic publishing advice, crop marks, comments, annotations, watermarks, or PDF-creation branding.
- Use vector lines and shapes where possible. Embed the fonts used when the available PDF tools support embedding.
- Inspect raster images at their placed size when the available tools can calculate effective resolution. Aim for at least 300 effective DPI for printable artwork; do not merely change DPI metadata.
- Keep all writing lines, labels, page numbers, and footer elements inside the safe margins. Nothing may be clipped or crowded into the binding.
- Use plain language appropriate for the stated reader. Do not make medical, legal, financial, safety, gardening-result, or income guarantees.

Generate and save the actual downloadable PDF. Inspect the finished file with the tools available, including its page size, exact ${blueprint.targetPageCount}-page count, manifest order, margins, and text clipping. Return the completed PDF file, then: Created: filename — page count. Add a Manual check line only when something remains unresolved.`;
}

export function composeCustomerFileCreationPrompt(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const plan = book ? project.production.interiorPlans[book.id] : undefined;
  const blueprint = buildAdaptivePageBlueprint(project, bookId);
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const palette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const approvedPlan = getPromptWorkForChannel(project, "pk-interior-01")?.response.trim();
  const isCreativeMarket = project.marketplace === "Creative Market";
  const isLegacyMultiChannel = project.marketplace === "Multi-channel";
  const isDirectPrintProduct =
    project.marketplace === "Shopify / direct" &&
    project.format === "Low-content paperback";
  const createsPackage = isCreativeMarket || isLegacyMultiChannel || isDirectPrintProduct;
  const output = isCreativeMarket
    ? "an organized ZIP containing the finished PDF, a one-page read-me, and a short licence note"
    : isLegacyMultiChannel
      ? "an organized master ZIP containing the finished PDF, a one-page read-me, and a short channel-notes file"
      : isDirectPrintProduct
        ? "an organized print-production ZIP containing the print-ready interior PDF, the approved cover artwork, and a one-page production read-me"
        : "the finished customer-ready PDF";
  const filename = `${promptFilename(book?.title || project.name)}${createsPackage ? " - files.zip" : ".pdf"}`;

  return `Create ${output} now. Do not return a plan, instructions, code, or sample pages. Generate the actual finished file.

Product: ${book?.title || "Untitled product"}
Subtitle: ${book?.subtitle || "None"}
Sales channel: ${project.marketplace}
Format: ${project.format}
Reader: ${compactText(project.audience || "Not provided", 320)}
Reader outcome: ${compactText(project.theme || "Not provided", 420)}
Product purpose: ${compactText(book?.differentiator || "Not provided", 420)}
Publisher notes: ${compactText(project.notes || "None", 700)}
File name: ${filename}
Page size: ${project.production.trimSize}
Finished PDF pages: exactly ${blueprint.targetPageCount}
Delivery mode: ${blueprint.mode === "reusable-digital" ? "a reusable digital product; do not pad it with identical blank duplicates" : "a complete bound-print production file"}
Visual style: ${system ? `${system.name} — ${system.character}` : "Clear and appropriate for the reader and subject"}
Product colours: ${palette?.colors.map((color) => `${color.name} ${color.hex}`).join("; ") || "Not selected"}

## Adaptive typography
${adaptiveInteriorTypographyBrief(project)}

## Channel-specific finishing rules
${customerFileChannelRules(project)}

${practicalFileCreationContract(createsPackage ? "finished customer package" : "finished customer PDF")}

## Mandatory page allocation
${blueprint.countSummary}

## Mandatory page specifications
${adaptivePageSpecifications(project, bookId)}

## Mandatory reading order
Publisher's earlier flow preference: ${compactText(plan?.sequenceNotes || "None recorded", 700)}
Calculated complete flow: ${blueprint.sequenceSummary}

Follow this exact page manifest:
${blueprint.manifest}

${approvedPlan ? `## Approved content details to preserve\n${compactText(approvedPlan, 4200)}\n` : ""}
## Production rules
- Create every finished page and all necessary reader-facing labels, instructions, fields, checkboxes, and page numbers.
- For a reusable digital product, include one reusable master for a recurring blank template. Any additional pages assigned to that function must be meaningfully different versions, examples, or levels—not identical copies.
- For a workbook, journal, or activity product, each assigned working page must move the reader forward or provide genuinely different content.
- Keep the design readable, printable, practical, and consistent with the selected audience, style, colours, and typography.
- Use available embeddable fonts, consistent margins, and no placeholder text, watermarks, crop marks, or PDF-creation branding.
- When the available tools can measure it, inspect raster images at their placed size and aim for at least 300 effective DPI for printable artwork; do not merely change DPI metadata.
- Include the promised read-me, licence, or channel notes when the output package calls for them.

Generate and save the actual downloadable ${createsPackage ? "ZIP package" : "PDF"}. Inspect the exact page count, manifest order, whether included files open, and whether text is clipped using the available tools. Return the completed file, then: Created: filename. Add a Manual check line only when something remains unresolved.`;
}

export function composeCoverPdfCreationPrompt(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const cover = book ? project.production.coverPlans[book.id] : undefined;
  const system = coverFamilySystems.find(
    (item) => item.id === project.production.coverSystemId,
  );
  const palette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const filename = `${promptFilename(book?.title || project.name)} - cover preview.png`;

  return `Create one full-cover layout preview image now. Return the image, not design advice.

I am attaching two files:
1. Finished front-cover artwork. Use this approved artwork as supplied; do not generate, redraw, or replace it.
2. Official KDP cover template. Use it as the visual guide for the back panel, spine, front panel, safe areas, and barcode area.

Book: ${book?.title || "Untitled book"}
Subtitle: ${book?.subtitle || "None"}
Publishing name: ${project.imprint.name || "None"}
Preview file name: ${filename}
Book size: ${project.production.trimSize}
Paper: ${project.production.paperType}
Interior pages: ${project.production.plannedPageCount}
Visual system: ${system ? `${system.name} — ${system.character}` : "Use the approved cover artwork"}
Composition system: ${system?.grid || "Keep a clear title area and preserve the supplied artwork's focal point"}
Cover variant: ${cover?.variant || "Use the strongest version of the selected visual system"}
Colours: ${palette?.colors.map((color) => `${color.name} ${color.hex}`).join("; ") || "Use the attached artwork"}
Back-cover text: ${compactText(cover?.backCoverCopy || "Not provided", 1400)}

## Typography selected by the app
${adaptiveCoverTypographyBrief(project, bookId)}

## Additional approved direction
Cover hierarchy: ${compactText(project.production.coverHierarchy || "Use the automatic title, subtitle, artwork, spine, and back-cover hierarchy above.", 1000)}
Back-cover structure: ${compactText(project.production.backCoverFramework || "Use the supplied wording in a clean, readable block; do not invent claims or filler.", 1200)}
Publisher cover rules: ${compactText(project.imprint.coverRules || "None supplied", 900)}
One extra cover instruction: ${compactText(project.production.coverNotes || "None supplied", 900)}

## Create the preview
1. Make one wide image following the attached template's proportions and panel order: back cover, spine, front cover.
2. Place the supplied artwork on the front without stretching it. Extend a simple matching background across the spine and back.
3. Add the title, subtitle, spine text when it visibly fits, and the supplied back-cover text. Use any close available font automatically. Keep the barcode area empty.
4. Use the template only as a placement guide, then remove all template lines from the preview.
5. Save and return one high-resolution PNG named ${filename}.

This PNG is a visual preview, not the final KDP upload file. The final cover will be rebuilt in Canva or another design tool using the official template. Start creating now without asking follow-up questions.`;
}

export function composePromptPlan(project: PaperKingdomProject, ids = project.promptPlanIds): string {
  const usedExtraIds = getPromptWorkEntriesForChannel(project)
    .filter(
      ([, work]) =>
        work.status !== "skipped" &&
        Boolean(work.copiedAt || work.response.trim()),
    )
    .map(([key]) => getPromptIdFromWorkKey(key));
  const selectedIds = [...new Set([...ids, ...usedExtraIds])];
  const selected = selectedIds
    .map((id) => allPrompts.find((prompt) => prompt.id === id))
    .filter((prompt): prompt is PromptDefinition => Boolean(prompt));
  const sequence = selected
    .map((prompt, index) => {
      const customization = project.promptCustomizations?.[prompt.id];
      const targetBook = customization?.targetBookId
        ? project.catalogue.find((book) => book.id === customization.targetBookId)
        : undefined;
      const scope = targetBook?.title
        ? `Specific title: ${targetBook.title}`
        : "Complete catalogue";
      const work = getPromptWorkForChannel(project, prompt.id);
      const status = work?.status === "skipped"
        ? "Not needed for this project"
        : work?.status === "approved"
        ? `Reviewed ${work.approvedAt || "date not recorded"}`
        : work?.response.trim()
          ? "Answer saved; review needed"
          : work?.copiedAt
            ? `Copied ${work.copiedAt}`
            : "Not yet used";

      return `${index + 1}. ${prompt.category} — ${prompt.title} (${scope}; ${status})`;
    })
    .join("\n");

  return `# ${project.name} — Prompts

Generated by Paper Kingdom ${project.productVersion}
Project revision: ${project.revision}
Generated: ${new Date().toISOString()}

> Copy each prompt into ChatGPT or another AI tool. Review every result for accuracy, originality, rights, quality, and current marketplace requirements before publishing.

## Suggested order

${sequence || "No prompts have been added to this plan."}

## How to use these prompts

1. Copy the first prompt into your chosen AI tool.
2. Read the result and correct anything inaccurate, unclear, or unsuitable.
3. Keep the decisions you approve nearby when you use the next prompt.
4. Check marketplace rules, claims, spelling, rights, and file specifications before publishing.

${selected
  .map((prompt, index) => {
    const work = getPromptWorkForChannel(project, prompt.id);
    const savedResult = work?.response.trim()
      ? `

### Saved AI answer

Status: ${work.status === "approved" ? "Reviewed and approved" : "Needs review"}
Copied: ${work.copiedAt || "Not recorded"}
Reviewed: ${work.approvedAt || "Not recorded"}

${work.response.trim()}`
      : "";
    return `---

## ${index + 1}. ${prompt.title}

${composePrompt(prompt, project, project.promptCustomizations?.[prompt.id])}${savedResult}`;
  })
  .join("\n\n")}`;
}
